import React, { useState, useEffect, useRef } from 'react';
import {
  Layout, Form, Input, Button, Card, Typography, message, Divider, Space,
  Modal, Radio, Row, Col, Tag, Tooltip, Select, Spin
} from 'antd';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { 
  ArrowLeftOutlined,
  EditOutlined,
  SaveOutlined,
  CameraOutlined,
  FlagOutlined,
  RetweetOutlined,
  LoadingOutlined,
  ZoomInOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScanResult, BusinessCard } from '../types/businessCard';
import { getRoutePath, ROUTES, API_PATHS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { hasAuth, AUTH } from '../utils/auth';
import { businessCardAPI } from '../services/api';
import { logApiError } from '../utils/errorLogger';
import { useContactUsers } from '../hooks/useContactUsers';
import axios from 'axios';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { optionFieldAPI, OptionFieldListResponse } from '../services/option-field-api';
import { AutoKanaHandler, hiraganaToKatakana, isHiraganaOnly } from '../utils/autoKana';
import { isInFlutter as isInFlutterUtil } from '../utils/flutterDetection';
import BusinessCardImageModal from '../components/BusinessCardImageModal';
import AdditionalInfoSection from '../components/AdditionalInfoSection';
import {
  OcrModel,
  getLastOcrUsedModel,
  setLastOcrUsedModel,
  getOcrModelPreference,
} from '../utils/ocrModel';
import { getEmergencyMode, getBusinessCardOcrType } from '../utils/chatGptOcr';

declare global {
  interface Window {
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
    FlutterNavigation?: {
      postMessage: (message: string) => void;
    };
    FlutterDebug?: {
      postMessage: (message: string) => void;
    };
    FlutterError?: {
      postMessage: (message: string) => void;
    };
    flutterFooterState?: {
      isKeyboardVisible: boolean;
      footerHeight: number;
    };
  }
}

const isSelected = (current: SelectionType, target: SelectionType): boolean => {
  return current === target;
};

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 郵便番号表示フォーマット（海外住所対応のため、入力された値をそのまま表示）
const formatPostalCodeDisplay = (code?: string): string => {
  if (!code) return '';
  return code;
};
// 電話番号表示フォーマット (google-libphonenumber)
const phoneUtil = PhoneNumberUtil.getInstance();
const formatPhoneNumberDisplay = (raw?: string, region = 'JP'): string => {
  if (!raw) return '';
  try {
    const num = phoneUtil.parseAndKeepRawInput(raw, region);
    return phoneUtil.format(num, PhoneNumberFormat.NATIONAL);
  } catch (e) {
    return raw;
  }
};
// Eメール検証用正規表現
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// 既存データの型
interface ExistingBusinessCard {
  id: string;
  cardData: Partial<BusinessCard>;
  lastUpdated: string;
}

// フィールド選択の状態管理用の型
type SelectionType = 'new' | 'existing';

// 選択から除外するフィールド
const EXCLUDED_FROM_SELECTION = ['representative', 'doNotDistribute'];

// 型安全な定数
const NEW_SELECTION: SelectionType = 'new';
const EXISTING_SELECTION: SelectionType = 'existing';

interface FieldSelection {
  [key: string]: SelectionType;
}

// OCRキーと実際のフィールドのマッピング
const ocrFieldMapping: { [key: string]: keyof BusinessCard } = {
  '会社名': 'company',
  '部署': 'department',
  '役職': 'position',
  '名前': 'name',
  '郵便番号': 'postalCode',
  '住所': 'address',
  '電話番号': 'phone',
  'FAX番号': 'fax',
  '携帯電話番号': 'mobile',
  'メールアドレス': 'email',
  'URL': 'url',
  '名刺登録日': 'businesscardcreatetime',
  '担当者': 'representative',
  '国': 'country',
};

// 本登録モードの「顧客備考」欄の重複整備（doMergeRemarks）設定の保存キー（クライアント側に前回設定を保持）
const STORAGE_KEY_PERMANENT_MERGE_REMARKS = 'permanentDoMergeRemarks';

// ビジネスカードデータをAPIリクエスト形式に変換するマッピング
const apiRequestMapping: { [key in keyof BusinessCard]?: string } = {
  'company': 'companyname',
  'department': 'deptname',
  'position': 'position',
  'name': 'customername',
  'postalCode': 'postcode',
  'address': 'address',
  'phone': 'tel',
  'fax': 'fax',
  'mobile': 'mobiletel',
  'email': 'mailaddress',
  'url': 'url',
  'country': 'countryname',
  'businesscardcreatetime': 'businesscardcreatetime',
  'businesscardfrontimage': 'businesscardfrontimage',
  'businesscardbackimage': 'businesscardbackimage',
};

// APIレスポンスのキーと実際のフィールドのマッピング
const apiFieldMapping: { [key: string]: keyof BusinessCard } = {
  'companyname': 'company',
  'deptname': 'department',
  'position': 'position',
  'customername': 'name',
  'postcode': 'postalCode',
  'address': 'address',
  'tel': 'phone',
  'fax': 'fax',
  'mobiletel': 'mobile',
  'mailaddress': 'email',
  'url': 'url',
  'businesscardcreatetime': 'businesscardcreatetime',
  'representative': 'representative',
  'countryname': 'country'
};

const EditResultPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); // ログインユーザー情報を取得
  // 担当者編集権限（権限なしの場合は担当者の追加/削除UIを非表示にする）
  const canEditContactUser = hasAuth(user, AUTH.FC_EDIT_CONTACTUSER);
  const [form] = Form.useForm<BusinessCard>();
  const [loading, setLoading] = useState<boolean>(false);
  
  // Flutter環境の検出
  const isInFlutter = isInFlutterUtil();
  
  // デバッグログ
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);
  const [reportContent, setReportContent] = useState<string>('');
  const [existingCard, setExistingCard] = useState<ExistingBusinessCard | null>(null);
  const [apiResponseData, setApiResponseData] = useState<any>(null);
  // 編集対象の顧客番号（新規登録時はnull、既存更新時は対象の顧客番号）
  const [editingCustomerNo, setEditingCustomerNo] = useState<number | null>(null);
  // 重複チェック時に除外する顧客番号（自分自身を除外するため）
  const [excludeFromDuplicateCheckCustomerNo, setExcludeFromDuplicateCheckCustomerNo] = useState<number | null>(null);
  const [fieldSelections, setFieldSelections] = useState<FieldSelection>({});
  const [formValidity, setFormValidity] = useState<{[key: string]: {valid: boolean, message?: string}}>({});
  const [submittingToApi, setSubmittingToApi] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [savedOcrData, setSavedOcrData] = useState<Partial<BusinessCard> | null>(null);
  // 登録済みデータ（追加情報画面等で保存した最新データ）
  const [savedRegisteredData, setSavedRegisteredData] = useState<Partial<BusinessCard> | null>(null);
  // 選択UI経由で値を変更中かどうか（手入力との区別用）
  const [isSelectingFromUI, setIsSelectingFromUI] = useState<boolean>(false);
  const { contactUsers, retiredUsers } = useContactUsers();
  // 選択された担当者
  const [selectedContactUsers, setSelectedContactUsers] = useState<number[]>([]);
  // 担当者追加用のドロップダウン表示制御
  const [addingContactUser, setAddingContactUser] = useState<boolean>(false);
  // 再読み取り中フラグ
  const [rescanning, setRescanning] = useState<boolean>(false);
  // 再解析の中断用（タイムアウト／キャンセルボタン共通）
  const rescanAbortRef = useRef<AbortController | null>(null);
  const rescanCancelledRef = useRef<boolean>(false);
  // 直近の OCR で使用されたモード（'fast' のときのみ高品質再解析ボタンを出す）
  const [lastOcrModel, setLastOcrModel] = useState<OcrModel | null>(null);
  // 裏面画像
  const [backSideImage, setBackSideImage] = useState<string | null>(null);

  // 直近の OCR で使用されたモードを IndexedDB から取得
  useEffect(() => {
    getLastOcrUsedModel().then(setLastOcrModel);
  }, []);

  // 緊急モード（ComSystemMapper の UseChatGPT が true）。true の間は OCR が ChatGPT で実行されるため、
  // 「高品質モードで再解析」ボタンを出さず、緊急モードで解析した旨の注記を表示する。
  const [emergencyMode, setEmergencyMode] = useState<boolean>(false);
  useEffect(() => {
    // 撮影時に確定した保持値を参照する（このページでは再取得しない）
    getEmergencyMode().then(setEmergencyMode);
  }, []);

  // フォロー登録から「戻る」で編集画面に戻ってきた場合、登録済みで
  // editingCustomerNo が入るため通常はロックが外れる。最初の登録時と同様に
  // 「電話番号のみ編集可」を再適用するためのフラグ。
  const [returnedFromFollowLock, setReturnedFromFollowLock] = useState<boolean>(false);
  useEffect(() => {
    (async () => {
      try {
        const flag = await indexedDBStorage.getItem('emergencyEditReturnLock');
        if (flag === 'true') {
          setReturnedFromFollowLock(true);
          // 消費して消す（再撮影/メニュー復帰など次フローには持ち越さない）
          await indexedDBStorage.removeItem('emergencyEditReturnLock');
        }
      } catch (e) {
        // 読めない場合は何もしない
      }
    })();
  }, []);

  // Flutterフッターメニューの状態（Flutter環境でのみ使用）
  const [flutterFooterHeight, setFlutterFooterHeight] = useState<number>(0);

  const [frontPreviewOpen, setFrontPreviewOpen] = useState<boolean>(false);
  const [backPreviewOpen, setBackPreviewOpen] = useState<boolean>(false);

  // 拡張フィールドの状態
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [loadingOptionFields, setLoadingOptionFields] = useState<boolean>(false);

  // フリガナフィールドの状態
  const [furiganaField, setFuriganaField] = useState<any | null>(null);
  const [furiganaValue, setFuriganaValue] = useState<string>('');
  const [furiganaNewValue, setFuriganaNewValue] = useState<string>(''); // OCRから取得した新規フリガナ
  const [furiganaExistingValue, setFuriganaExistingValue] = useState<string>(''); // APIから取得した既存フリガナ
  const [furiganaSelection, setFuriganaSelection] = useState<SelectionType>('new'); // フリガナの選択状態

  // 自動カタカナ変換ハンドラ（顧客名フィールド用）
  const autoKanaHandlerRef = React.useRef<AutoKanaHandler>(new AutoKanaHandler());
  // 重複チェックのdebounceタイマー
  const duplicateCheckTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 手動入力モード
  const [isManualInput, setIsManualInput] = useState<boolean>(false);

  // 本登録モード（仮登録顧客一覧の編集ボタンから入った場合）
  // 「再撮影」を「一覧に戻る」に置換し、最終モーダルをスキップして一覧に戻すために使用
  const [isPermanentMode, setIsPermanentMode] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const flag = await indexedDBStorage.getItem('permanentRegistrationMode');
      setIsPermanentMode(flag === 'true');
    })();
  }, []);

  // 本登録モード時の AdditionalInfoSection 用
  const [permanentOptionFieldValues, setPermanentOptionFieldValues] = useState<Record<string, any>>({});
  // 備考の反映先チェックボックス（両方ON=新規・既存とも反映 / 片方のみONも可）。デフォルトは両方ON
  const [permanentRemarksNewChecked, setPermanentRemarksNewChecked] = useState<boolean>(true);
  const [permanentRemarksExistingChecked, setPermanentRemarksExistingChecked] = useState<boolean>(true);
  const [permanentNewRemarks, setPermanentNewRemarks] = useState<string>('');
  const [permanentExistingRemarks, setPermanentExistingRemarks] = useState<string>('');
  // 「顧客備考」欄の重複整備を行うか（デフォルトON）。前回の設定を localStorage に保持する
  const [permanentDoMergeRemarks, setPermanentDoMergeRemarks] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_PERMANENT_MERGE_REMARKS);
    return saved === null ? true : saved === 'true';
  });
  const handlePermanentDoMergeRemarksChange = (checked: boolean) => {
    setPermanentDoMergeRemarks(checked);
    localStorage.setItem(STORAGE_KEY_PERMANENT_MERGE_REMARKS, String(checked));
  };
  // 本登録モードで既存顧客（重複）が存在するか（備考トグル表示の判定に使用）
  const [permanentHasExisting, setPermanentHasExisting] = useState<boolean>(false);
  // 本登録モードでの名刺画像の選択（'new'=仮登録側 / 'existing'=既存顧客側）
  const [permanentCardImageSelection, setPermanentCardImageSelection] = useState<'new' | 'existing'>('new');
  // 仮登録「次へ」用の再ロードキー（次の顧客に切り替えた際の初期化トリガ）
  const [reloadCounter, setReloadCounter] = useState<number>(0);
  // 「本登録して次へ」処理中フラグ
  const [advancingToNext, setAdvancingToNext] = useState<boolean>(false);
  // 仮登録の本登録（regist）のレスポンス保持用。次の仮登録顧客はこのレスポンスに準ずる
  const permanentRegistResponseRef = useRef<any>(null);

  // 本登録モード時に scanResult から AdditionalInfoSection の初期値（rank/notes/optionfield、既存備考）を取り出してフォームに反映
  useEffect(() => {
    if (!isPermanentMode) return;
    if (!scanResult || !scanResult.apiResponse) return;
    const cd: any = scanResult.apiResponse.customerdata || {};
    const dd: any =
      (scanResult.apiResponse as any).duplicatedata ||
      (scanResult.apiResponse as any).duplicatelist?.[0] ||
      null;
    setPermanentHasExisting(!!dd);
    // rank/notes
    // ランクは既存顧客(dd)があれば既存側を優先表示。無ければ新規(仮登録)側。
    const rankSource = (dd && dd.rankno !== undefined) ? dd.rankno : cd.rankno;
    if (rankSource !== undefined) form.setFieldsValue({ rankno: rankSource });
    const newRemarks: string = (cd.remarks ?? '') as string;
    const existingRemarks: string = (dd?.remarks ?? '') as string;
    setPermanentNewRemarks(newRemarks);
    setPermanentExistingRemarks(existingRemarks);
    // 備考は初期で新規・既存とも反映（両方ON）
    setPermanentRemarksNewChecked(true);
    setPermanentRemarksExistingChecked(true);
    // 「顧客備考」欄の重複整備は前回の設定（localStorage）に従う（未保存はON）
    const savedMerge = localStorage.getItem(STORAGE_KEY_PERMANENT_MERGE_REMARKS);
    setPermanentDoMergeRemarks(savedMerge === null ? true : savedMerge === 'true');
    // 名刺画像も初期は新規（仮登録）側を選択
    setPermanentCardImageSelection('new');
    form.setFieldsValue({ notes: newRemarks });
    // 拡張フィールド: 既存顧客（重複）があればその optionfield を表示。無ければ仮登録の値。
    const raw = dd ? dd.optionfield : cd.optionfield;
    let opt: Record<string, any> = {};
    if (typeof raw === 'string' && raw) {
      try {
        opt = JSON.parse(raw);
      } catch {
        opt = {};
      }
    } else if (raw && typeof raw === 'object') {
      opt = raw;
    }
    setPermanentOptionFieldValues(opt);
  }, [isPermanentMode, scanResult, form]);

  // 本登録モード時に「一覧に戻る」を実行する処理（再撮影ボタン置換・戻るボタン共通）
  const handleReturnToTempCustomerList = async () => {
    await indexedDBStorage.removeItem('permanentRegistrationMode');
    await indexedDBStorage.removeItem('scanResult');
    await indexedDBStorage.removeItem('editResultBackup');
    await indexedDBStorage.removeItem('tempRegistrationList');
    await indexedDBStorage.removeItem('tempRegistrationCurrentNo');
    await indexedDBStorage.removeItem('tempRegistrationDateFilter');
    // 戻ったメニューで一覧モーダルを自動オープン
    await indexedDBStorage.setItem('reopenTempCustomerListAfterReturn', 'true');
    navigate(getRoutePath('/menu'));
  };

  // 「本登録して次へ」: 登録成功後に、regist のレスポンスが返す次の仮登録顧客へ自動で切替
  // 次が無い場合はメニューに戻り一覧モーダルを再オープン
  const handleAdvanceToNextTempCustomer = async () => {
    try {
      setAdvancingToNext(true);

      // 取込日の絞り込みが指定されている場合は、regist のレスポンス（日付条件を持たない）ではなく
      // 一覧で保存した「絞り込み済み（日付フィルタ済み）リスト」の順に次の顧客へ進める。
      // これにより、日付範囲外の顧客が「次」に出てくるのを防ぐ。
      let nextNo: number | undefined;
      const dateFilterRaw = await indexedDBStorage.getItem('tempRegistrationDateFilter');
      let hasDateFilter = false;
      try {
        const df = dateFilterRaw ? JSON.parse(dateFilterRaw) : null;
        hasDateFilter = !!(df && (df.startDate || df.endDate));
      } catch {
        hasDateFilter = false;
      }

      if (hasDateFilter) {
        // 絞り込み済みリストから現在の顧客の次を取得（リストは日付・担当者で絞り込み済み）
        const listRaw = await indexedDBStorage.getItem('tempRegistrationList');
        const currentRaw = await indexedDBStorage.getItem('tempRegistrationCurrentNo');
        let list: Array<{ customerno: number }> = [];
        try {
          list = listRaw ? JSON.parse(listRaw) : [];
        } catch {
          list = [];
        }
        const currentNo = currentRaw ? parseInt(currentRaw, 10) : NaN;
        const idx = list.findIndex((x) => x.customerno === currentNo);
        nextNo = idx >= 0 ? list[idx + 1]?.customerno : undefined;
      } else {
        // 「次の仮登録顧客」は regist のレスポンス（customerdata）に準ずる
        const registResponse = permanentRegistResponseRef.current;
        nextNo = registResponse?.customerdata?.customerno;
      }

      if (!nextNo) {
        // 次が無い→一覧へ戻る
        await handleReturnToTempCustomerList();
        return;
      }

      // regist レスポンスの customerdata は contactusernoArray など項目構成が select と異なり、
      // そのまま使うと本登録ペイロードが不足する。次の customerno で明細を取り直して
      // モーダルからの編集フロー（selectTemporaryCustomer）と同じデータ形状にそろえる。
      const detail = await businessCardAPI.selectTemporaryCustomer(nextNo);
      if (detail?.statuscode !== 200 || !detail.customerdata) {
        message.error(detail?.message || '次の仮登録顧客の明細取得に失敗しました');
        return;
      }
      const cd = detail.customerdata;
      const dd = detail.duplicatedata;

      const newScanResult: any = {
        success: true,
        data: cd,
        imageUrl: cd.businesscardfrontimage || '',
        apiResponse: {
          customerdata: cd,
          duplicatedata: dd || undefined,
          duplicatelist: dd ? [dd] : [],
        },
      };
      await indexedDBStorage.setItem('scanResult', JSON.stringify(newScanResult));
      await indexedDBStorage.removeItem('editResultBackup');
      await indexedDBStorage.removeItem('editingCustomerNo');
      await indexedDBStorage.setItem('tempRegistrationCurrentNo', String(cd.customerno));

      // 既存のフォーム状態をクリアして次の顧客で再初期化
      form.resetFields();
      // 前の顧客の「既存側」stateが次の顧客（既存なし）に残らないよう明示クリア（本登録モード専用）。
      // fetchScanResult は既存データがある時しか set しないため、ここで初期化しておく。
      setApiResponseData(null);
      setSavedRegisteredData(null);
      setFuriganaExistingValue('');
      setReloadCounter((c) => c + 1);
      // 次の顧客の内容に切り替わったらページ最上部へスクロール
      window.scrollTo(0, 0);
      message.success('次の仮登録顧客に切り替えました');
    } catch (e) {
      console.error('次の仮登録顧客への切替エラー:', e);
      message.error('次の仮登録顧客への切替に失敗しました');
    } finally {
      setAdvancingToNext(false);
    }
  };

  // 郵便番号で住所自動入力
  const [postalCodeConfirmVisible, setPostalCodeConfirmVisible] = useState<boolean>(false);
  const [newAddress, setNewAddress] = useState<string>('');

  // データフィールドの定義
  const dataFields = [
    { key: 'company', label: '会社名' },
    { key: 'department', label: '部署' },
    { key: 'position', label: '役職' },
    { key: 'name', label: '顧客名', required: true },
    { key: 'postalCode', label: '郵便番号' },
    { key: 'address', label: '住所' },
    { key: 'phone', label: '電話番号', required: true, conditionalRequired: true },
    { key: 'fax', label: 'FAX番号' },
    { key: 'mobile', label: '携帯電話番号' },
    { key: 'email', label: 'メールアドレス', required: true, conditionalRequired: true },
    { key: 'url', label: 'URL' },
    ...(!isManualInput ? [{ key: 'businesscardcreatetime', label: '名刺登録日' }] : []),
    { key: 'representative', label: '担当者' },
    { key: 'country', label: '国' },
    { key: 'doNotDistribute', label: '配信NG' }
  ];

  // ===== 緊急モード（UseChatGPT=true）の初回登録時の挙動 =====
  // 初回登録（撮影直後の新規。本登録モードでも既存編集でもない）かどうか
  const isInitialRegistration = !isPermanentMode && !editingCustomerNo;
  // 既存顧客が見つかっているか（重複チェック結果）
  const hasExistingDuplicate = !!(apiResponseData && apiResponseData.customerno);
  // 緊急モードの初回登録でのロック種別
  //  - 全ロック: 既存顧客が見つかった場合（既存側を全選択＆全項目編集不可）
  //  - 電話のみ: 全く新規の場合（電話番号のみ編集可）
  const emergencyLockAll = emergencyMode && isInitialRegistration && hasExistingDuplicate;
  // 初回登録時の電話のみ編集可、または「フォロー登録から戻ってきた」場合も電話のみ編集可にする。
  // （登録後は editingCustomerNo が入り isInitialRegistration=false になるため、戻り時フラグで補う）
  const emergencyPhoneOnly =
    emergencyMode &&
    ((isInitialRegistration && !hasExistingDuplicate) || returnedFromFollowLock);
  // 既存検知モーダルの表示制御（静的 Modal.info はこのアプリでは描画されないため制御コンポーネントで出す）
  const [emergencyModalOpen, setEmergencyModalOpen] = useState<boolean>(false);

  // 既存顧客検知時：モーダルを一度だけ表示し、既存側を全選択にする
  // （名刺画像・名刺交換日は新規側のまま＝businesscardcreatetime/representative は除外）
  const emergencyExistingModalShownRef = useRef<boolean>(false);
  useEffect(() => {
    if (!emergencyLockAll) {
      emergencyExistingModalShownRef.current = false;
      return;
    }
    setFieldSelections(prev => {
      const next = { ...prev };
      dataFields.forEach(f => {
        if (f.key !== 'businesscardcreatetime' && f.key !== 'representative') {
          next[f.key] = 'existing';
        }
      });
      return next;
    });
    if (!emergencyExistingModalShownRef.current) {
      emergencyExistingModalShownRef.current = true;
      setEmergencyModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emergencyLockAll]);

  // Flutterフッター状態の監視（Flutter環境でのみ）
  useEffect(() => {
    if (!isInFlutter) return;

    const handleFlutterFooterStateChange = (event: any) => {
      const { footerHeight } = event.detail;
      setFlutterFooterHeight(footerHeight || 0);
    };

    // イベントリスナーを登録
    window.addEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);

    // 初期値の取得（Flutterから既に通知されている可能性がある）
    const initialState = (window as any).flutterFooterState;
    if (initialState && initialState.footerHeight !== undefined) {
      setFlutterFooterHeight(initialState.footerHeight);
    }

    return () => {
      window.removeEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);
    };
  }, [isInFlutter]);


  // 拡張フィールド一覧を取得するuseEffect
  useEffect(() => {
    const fetchOptionFields = async () => {
      setLoadingOptionFields(true);
      try {
        const optionFieldResult = await optionFieldAPI.getOptionFieldList();
        console.log('EditResultPage - 拡張フィールド一覧取得結果:', optionFieldResult);

        if (optionFieldResult.statuscode === 200) {
          setOptionFields(optionFieldResult);
          console.log('EditResultPage - 顧客拡張フィールド:', optionFieldResult.customeroptionfieldlist);
          console.log('EditResultPage - フォロー拡張フィールド:', optionFieldResult.followoptionfieldlist);

          // furiganaフィールドを抽出
          if (optionFieldResult.customeroptionfieldlist) {
            const furiganaFieldData = optionFieldResult.customeroptionfieldlist.find(
              (field: any) => field.optionfieldkey === 'furigana'
            );
            if (furiganaFieldData) {
              console.log('EditResultPage - フリガナフィールド検出:', furiganaFieldData);
              setFuriganaField(furiganaFieldData);
            }
          }
        } else {
          console.error('EditResultPage - 拡張フィールド取得エラー:', optionFieldResult.message);
        }
      } catch (error) {
        console.error('EditResultPage - 拡張フィールド取得API呼び出しエラー:', error);
        logApiError('EditResultPage.fetchOptionFields', error, '/api/getOptionFieldList', 'GET');
      } finally {
        setLoadingOptionFields(false);
      }
    };

    fetchOptionFields();
  }, []);

  // IndexedDBからスキャン結果を取得するuseEffect
  useEffect(() => {
    const fetchScanResult = async () => {
      // フォーム状態をクリアして初期状態に戻す
      form.resetFields();
      // scanResultをバックアップキーから取得(初回のみscanResultを別キーに保存)
      const backupKey = 'editResultBackup';
      let storedJson = await indexedDBStorage.getItem(backupKey);
      const origJson = await indexedDBStorage.getItem('scanResult');
      if (!storedJson && origJson) {
        await indexedDBStorage.setItem(backupKey, origJson);
        storedJson = origJson;
      }
      // バックアップされたデータを使用
      const storedResult = storedJson;

    if (storedResult) {
      try {
        const result: ScanResult = JSON.parse(storedResult);
        setScanResult(result);

        // ===== 顧客情報データのコンソール出力 =====
        console.log('===== EditResultPage - データ読み込み開始 =====');
        console.log('EditResultPage - スキャン結果全体:', result);
        console.log('EditResultPage - OCRデータ:', result.data);
        console.log('EditResultPage - APIレスポンス:', result.apiResponse);
        if (result.apiResponse) {
          console.log('EditResultPage - 顧客データ:', result.apiResponse.customerdata);
          console.log('EditResultPage - 重複データ:', result.apiResponse.duplicatedata);
          console.log('EditResultPage - 重複リスト:', result.apiResponse.duplicatelist);
        }
        console.log('==========================================');

        // IndexedDBから編集対象顧客番号を復元
        const storedEditingCustomerNo = await indexedDBStorage.getItem('editingCustomerNo');
        const storedExcludeCustomerNo = await indexedDBStorage.getItem('excludeFromDuplicateCheckCustomerNo');

        // 新規登録モードか既存編集モードかを判定
        // customerdataが存在する = 既存顧客編集モード（登録済み）
        // customerdataがない = 新規登録モード（まだ登録していない）
        const isExistingCustomerMode = result.apiResponse &&
                                       (result.apiResponse.customerdata !== undefined &&
                                        result.apiResponse.customerdata !== null);

        if (isExistingCustomerMode) {
          // 既存顧客編集モード：IndexedDBから復元
          if (storedEditingCustomerNo) {
            const editingNo = parseInt(storedEditingCustomerNo, 10);
            setEditingCustomerNo(editingNo);
            console.log('EditResultPage - 編集対象顧客番号を復元（既存編集モード）:', editingNo);
          } else {
            setEditingCustomerNo(null);
          }
          if (storedExcludeCustomerNo) {
            const excludeNo = parseInt(storedExcludeCustomerNo, 10);
            setExcludeFromDuplicateCheckCustomerNo(excludeNo);
            console.log('EditResultPage - 重複チェック除外対象顧客番号を復元（既存編集モード）:', excludeNo);
          } else {
            setExcludeFromDuplicateCheckCustomerNo(null);
          }
        } else {
          // 新規登録モード：強制的にnullに設定（IndexedDBの古い値を無視）
          setEditingCustomerNo(null);
          setExcludeFromDuplicateCheckCustomerNo(null);
          console.log('EditResultPage - 新規顧客モード（IndexedDBの値をクリア）');

          // IndexedDBの古い値もクリア
          if (storedEditingCustomerNo) {
            await indexedDBStorage.removeItem('editingCustomerNo');
            console.log('EditResultPage - IndexedDBのeditingCustomerNoをクリア');
          }
          if (storedExcludeCustomerNo) {
            await indexedDBStorage.removeItem('excludeFromDuplicateCheckCustomerNo');
            console.log('EditResultPage - IndexedDBのexcludeFromDuplicateCheckCustomerNoをクリア');
          }
        }

        // 重複リストの情報はduplicatelistとduplicatedataに保持（編集対象顧客番号には影響しない）
        if (result.apiResponse && result.apiResponse.duplicatelist && result.apiResponse.duplicatelist.length > 0) {
          console.log('EditResultPage - 重複リストあり:', result.apiResponse.duplicatelist.length, '件');
        } else {
          console.log('EditResultPage - 重複リストなし');
        }
        
        // OCRデータをビジネスカードデータ形式に変換
        const convertedData: Partial<BusinessCard> = {};

        if (result.data) {
          console.log('EditResultPage - OCRデータをBusinessCard形式に変換開始');
          // OCRデータをマッピング
          Object.entries(result.data as Record<string, any>).forEach(([key, value]) => {
            const mappedKey = ocrFieldMapping[key];
            if (mappedKey && value !== null && value !== undefined && value !== "null") {
              // 型安全に値を代入
              const stringValue = String(value);
              (convertedData as any)[mappedKey] = stringValue;
              console.log(`EditResultPage - マッピング: ${key} -> ${mappedKey} = ${stringValue}`);
            }
          });
          console.log('EditResultPage - 変換後のデータ:', convertedData);
        }

        // 手動入力モードかチェック
        const isManualInput = await indexedDBStorage.getItem('isManualInput');
        setIsManualInput(isManualInput === 'true');

        // オプションを追加
        if (!convertedData.businesscardcreatetime && isManualInput !== 'true') {
          // 現在の日付と時刻をフォーマット (YYYY-MM-DD HH:MM:SS)
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          
          // 日付と時刻を含めたフォーマット
          const dateTimeString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          
          (convertedData as any).businesscardcreatetime = dateTimeString;
        }
        
        // 担当者情報を追加
        if (!convertedData.representative && user?.name) {
          (convertedData as any).representative = user.name;
        }

        // 担当者IDの配列を設定（優先順位: customerdata > duplicatedata > duplicatelist[0]）
        // API仕様に合わせ contactusernoArray / contactUserList を読む（contactuserno はOCR系のフォールバック）
        const contactUsernoSource =
          result.apiResponse?.customerdata ||
          result.apiResponse?.duplicatedata ||
          (result.apiResponse?.duplicatelist?.[0] ?? null);
        const contactUserIds = extractContactUserIds(contactUsernoSource);
        // 本登録モードでは担当者は専用effect（既存側 duplicatedata を優先）で設定するため、ここでは上書きしない。
        const _permModeForContacts = await indexedDBStorage.getItem('permanentRegistrationMode');
        if (_permModeForContacts !== 'true') {
          if (contactUserIds.length > 0) {
            // 登録済み（isExistingCustomerMode）の場合はそのまま使用
            // 新規の場合のみログインユーザーを追加
            if (!isExistingCustomerMode && user?.id && !contactUserIds.includes(user.id)) {
              contactUserIds.push(user.id);
            }
            setSelectedContactUsers(contactUserIds);
          } else if (user?.id) {
            // 担当者情報がない場合はログインユーザーのみ
            setSelectedContactUsers([user.id]);
          }
        }

        // 国情報を追加（住所から判断）
        // ただし、登録済みデータがある場合はスキップ（ユーザーが意図的に空にした可能性があるため）
        const shouldAutoFillCountry = !(result.apiResponse && (result.apiResponse.customerdata || result.apiResponse.duplicatedata));

        if (!convertedData.country && shouldAutoFillCountry) {
          if (convertedData.address) {
            // 住所に「都」「府」「県」が含まれていれば日本と判定
            if (
              convertedData.address.includes('都') ||
              convertedData.address.includes('府') ||
              convertedData.address.includes('県') ||
              convertedData.address.includes('市') ||
              convertedData.address.includes('区')
            ) {
              (convertedData as any).country = '日本';
            }
          } else {
            // 住所がない場合もデフォルトで日本
            (convertedData as any).country = '日本';
          }
        }

        if (convertedData.address) {
          // Check if address already has prefecture information
          const hasJapanesePrefecture = 
            convertedData.address.includes('都') || 
            convertedData.address.includes('府') || 
            convertedData.address.includes('県') ||
            convertedData.address.includes('道');
          
          // 初期化時の郵便番号から住所検索機能（コメントアウト）
          /*
          // If address doesn't have prefecture but we have postal code, try to get prefecture
          if (!hasJapanesePrefecture && convertedData.postalCode) {
            // Try to get prefecture from postal code
            const postalCode = convertedData.postalCode;
            fetchAddressFromPostalCode(postalCode).then(fullAddress => {
              if (fullAddress) {
                const prefecture = extractPrefecture(fullAddress);
                if (prefecture) {
                  // Combine prefecture with current address
                  const currentAddress = String(convertedData.address);
                  const newAddress = `${prefecture}${currentAddress}`;

                  // Update form with new address
                  form.setFieldsValue({ address: newAddress });

                  // Also update convertedData for consistency
                  (convertedData as any).address = newAddress;

                  //
                }
              }
            }).catch(error => {
            });
          }
          */
        }

        // フォームに値をセット
        // 登録済みデータ（apiResponse.customerdataまたはduplicatedata）がある場合は、それを優先して使用
        let initialFormData: Partial<BusinessCard> = convertedData;
        let hasRegisteredData = false;

        if (result.apiResponse && (result.apiResponse.customerdata || result.apiResponse.duplicatedata)) {
          const registeredApiData = result.apiResponse.customerdata || result.apiResponse.duplicatedata;
          console.log('EditResultPage - 登録済みデータを検出:', registeredApiData);

          // APIデータをBusinessCard形式に変換
          const registeredData: Partial<BusinessCard> = {};
          Object.entries(registeredApiData).forEach(([apiKey, value]) => {
            const fieldKey = apiFieldMapping[apiKey];
            if (fieldKey && value !== undefined && value !== null) {
              // 日付フィールドの場合はISO形式からYYYY-MM-DD HH:MM:SS形式に変換
              if (fieldKey === 'businesscardcreatetime' && typeof value === 'string') {
                try {
                  const date = new Date(value);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  const seconds = String(date.getSeconds()).padStart(2, '0');
                  (registeredData as any)[fieldKey] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                } catch (e) {
                  (registeredData as any)[fieldKey] = String(value);
                }
              } else {
                (registeredData as any)[fieldKey] = String(value);
              }
            }
          });

          // maildeliveryflag を doNotDistribute に変換（逆のロジック）
          if (registeredApiData.maildeliveryflag !== undefined) {
            (registeredData as any).doNotDistribute = !registeredApiData.maildeliveryflag;
          }

          console.log('EditResultPage - 登録済みデータ（変換後）:', registeredData);

          // 登録済みデータがあればそれを優先
          if (Object.keys(registeredData).length > 0) {
            initialFormData = { ...convertedData, ...registeredData };
            setSavedRegisteredData(registeredData);
            hasRegisteredData = true;
            console.log('EditResultPage - 登録済みデータを初期値として使用');
          }
        }

        console.log('EditResultPage - フォームに初期値を設定:', initialFormData);
        form.setFieldsValue(initialFormData);
        setSavedOcrData(convertedData);
        console.log('EditResultPage - savedOcrDataに保存:', convertedData);
        if (hasRegisteredData) {
          console.log('EditResultPage - savedRegisteredDataに保存');
        }

        // 登録済みデータのoptionfieldからフリガナを復元
        if (result.apiResponse && (result.apiResponse.customerdata || result.apiResponse.duplicatedata)) {
          const registeredApiData = result.apiResponse.customerdata || result.apiResponse.duplicatedata;
          if (registeredApiData.optionfield) {
            console.log('EditResultPage - 登録済みoptionfield(raw):', registeredApiData.optionfield);
            try {
              const optionField = typeof registeredApiData.optionfield === 'string'
                ? JSON.parse(registeredApiData.optionfield)
                : registeredApiData.optionfield;
              console.log('EditResultPage - 登録済みoptionfield(parsed):', optionField);
              // furiganaフィールドが存在する場合（値が空でも）設定
              if ('furigana' in optionField) {
                const furiganaVal = optionField.furigana || '';
                console.log('EditResultPage - 登録済みfurigana値検出:', furiganaVal);
                setFuriganaExistingValue(furiganaVal);
                setFuriganaNewValue(furiganaVal);
                setFuriganaValue(furiganaVal);
                console.log('EditResultPage - furiganaExistingValue, furiganaNewValue, furiganaValueを設定');
              }
            } catch (error) {
              console.error('EditResultPage - 登録済みoptionfield解析エラー:', error);
            }
          }
        }

        // 配信NGのデフォルト値を設定（登録済みデータがない場合のみ）
        if (!hasRegisteredData) {
          if (result.apiResponse && result.apiResponse.duplicatelist && result.apiResponse.duplicatelist.length > 0) {
            form.setFieldsValue({
              doNotDistribute: result.apiResponse.duplicatelist[0].maildeliveryflag === false
            });
          } else {
            form.setFieldsValue({ doNotDistribute: false });
          }
        }

        // APIレスポンスからデータを抽出
        if (result.apiResponse && result.apiResponse.duplicatelist && result.apiResponse.duplicatelist.length > 0) {
          const duplicateData = {...result.apiResponse.duplicatelist[0]};
          console.log('EditResultPage - 重複データを設定:', duplicateData);
          console.log('EditResultPage - 重複データの全フィールド:', Object.keys(duplicateData));

          if (duplicateData.optionfield) {
            console.log('EditResultPage - optionfield(raw):', duplicateData.optionfield);
            try {
              const optionField = JSON.parse(duplicateData.optionfield);
              console.log('EditResultPage - optionfield(parsed):', optionField);
              // furiganaフィールドが存在する場合（値が空でも）設定
              if ('furigana' in optionField) {
                const furiganaVal = optionField.furigana || '';
                console.log('EditResultPage - 既存furigana値検出:', furiganaVal);
                setFuriganaExistingValue(furiganaVal);
                // 新規側は空のまま（OCRデータがないため）
                setFuriganaNewValue('');
                setFuriganaValue('');
                // 新規を選択（ユーザーが選択できるように）
                setFuriganaSelection('new');
              }
            } catch (error) {
              console.error('EditResultPage - optionfield解析エラー:', error);
            }
          }
          setApiResponseData(duplicateData);
          console.log('EditResultPage - apiResponseDataに設定完了');
        }

        // OCRから取得した新規フリガナ（現時点では空）
        // 登録済みデータや重複データから復元されない場合は、useStateの初期値（空文字）のまま

        // 初期フィールド選択を設定（すべて新規、担当者フィールドを除く）
        const initialSelections: FieldSelection = {};
        dataFields.forEach(field => {
          if (!EXCLUDED_FROM_SELECTION.includes(field.key)) {
            initialSelections[field.key] = 'new';
          }
        });
        console.log('EditResultPage - 初期フィールド選択状態:', initialSelections);
        setFieldSelections(initialSelections);
        
        // ページ実行時に重複チェックを実行（顧客番号が設定された状態で）
        setTimeout(() => {
          // 重複チェック実行時に現在の顧客IDをコンソール出力
          checkDuplicateCustomer();
        }, 100);
        
      } catch (error) {
        message.error('データの読み込みに失敗しました');
        navigate(getRoutePath('/camera'));
      }
    } else {
      message.error('撮影データが見つかりません');
      navigate(getRoutePath('/camera'));
    }

    // 裏面画像の読み込み
    const loadBackSideImage = async () => {
      try {
        const backSideImageData = await indexedDBStorage.getItem('backSideImage');
        if (backSideImageData) {
          setBackSideImage(backSideImageData);
        }
      } catch (error) {
        console.error('EditResultPage - 裏面画像の読み込みに失敗:', error);
      }
    };
    loadBackSideImage();

    // 裏面撮影から戻ってきた時のフォーム値復元
    const restoreFormValues = async () => {
      try {
        const tempFormValuesJson = await indexedDBStorage.getItem('tempFormValues');
        const tempFieldSelectionsJson = await indexedDBStorage.getItem('tempFieldSelections');

        if (tempFormValuesJson) {
          const tempFormValues = JSON.parse(tempFormValuesJson);
          form.setFieldsValue(tempFormValues);
          await indexedDBStorage.removeItem('tempFormValues');
        }

        if (tempFieldSelectionsJson) {
          const tempFieldSelectionsData = JSON.parse(tempFieldSelectionsJson);
          setFieldSelections(tempFieldSelectionsData);
          await indexedDBStorage.removeItem('tempFieldSelections');
        }
      } catch (error) {
        console.error('EditResultPage - フォーム値復元エラー:', error);
      }
    };
    restoreFormValues();
    };

    fetchScanResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, navigate, user, location.key, reloadCounter]);

  // contactusernoの値をnumber[]に変換するヘルパー関数
  const parseContactUserIds = (val: any): number[] => {
    if (!val) return [];
    try {
      if (typeof val === 'string') {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed.map(Number) : [Number(parsed)];
      }
      if (Array.isArray(val)) return val.map(Number);
    } catch {
      return String(val)
        .replace(/[\[\]]/g, '')
        .split(',')
        .map((id: string) => parseInt(id.trim(), 10))
        .filter((id: number) => !isNaN(id));
    }
    return [];
  };

  // 担当者IDをAPIレスポンスの形状に合わせて取り出す。
  // 仮登録（customercompare/regist・select）は contactusernoArray（JSON文字列 "[-9003]"）と
  // contactUserList（[{userno,username}]）を返す。OCR・既存顧客編集系は contactuserno を返すため、
  // contactusernoArray → contactUserList → contactuserno の順で対応する。
  const extractContactUserIds = (source: any): number[] => {
    if (!source) return [];
    const arr = source.contactusernoArray;
    if (arr !== undefined && arr !== null && arr !== '') {
      return parseContactUserIds(arr);
    }
    if (Array.isArray(source.contactUserList)) {
      return source.contactUserList
        .map((u: any) => Number(u?.userno))
        .filter((n: number) => !Number.isNaN(n));
    }
    return parseContactUserIds(source.contactuserno);
  };
  
  // API応答データまたは登録済みデータから実際のビジネスカードフィールドへの値を取得する関数
  // 優先順位: apiResponseData（重複データ） > savedRegisteredData（登録済みデータ）
  const getApiFieldValue = (fieldKey: string): string | null => {
    // 1. 重複データ（apiResponseData）から取得
    if (apiResponseData) {
      const apiKey = Object.entries(apiFieldMapping).find(([_, value]) => value === fieldKey)?.[0];
      if (apiKey) {
        if (apiKey in apiResponseData) {
          const value = apiResponseData[apiKey];
          // null または undefined の場合のみ null を返す
          // 空文字列の場合は空文字列として返す（既存側に「-」を表示するため）
          if (value !== null && value !== undefined) {
            return String(value);
          }
        }
        // 重複データが存在するが、このフィールドがない場合は空文字列を返す
        // （既存側に「-」を表示するため）
        return '';
      }
    }

    // 2. 登録済みデータ（savedRegisteredData）から取得
    if (savedRegisteredData) {
      if (fieldKey in savedRegisteredData) {
        const value = (savedRegisteredData as Record<string, any>)[fieldKey];
        // null または undefined の場合のみ null を返す
        // 空文字列の場合は空文字列として返す（既存側に「-」を表示するため）
        if (value !== null && value !== undefined) {
          return String(value);
        }
      }
      // 登録済みデータが存在するが、このフィールドがない場合は空文字列を返す
      // （既存側に「-」を表示するため）
      return '';
    }

    return null;
  };

  // 既存顧客の担当者IDリストを取得する関数（getApiFieldValueと同じ優先順位）
  // 優先順位: apiResponseData（重複データ） > savedRegisteredData（登録済みデータ）
  const getApiContactUserIds = (): number[] | null => {
    const source = apiResponseData || savedRegisteredData;
    if (!source) return null;
    const ids = extractContactUserIds(source);
    return ids.length > 0 ? ids : null;
  };
  
  // OCRデータから値を取得する関数
  const getOCRFieldValue = (fieldKey: string): string | null => {
    // savedOcrDataから値を取得
    if (savedOcrData && 
        savedOcrData[fieldKey as keyof BusinessCard] !== undefined && 
        savedOcrData[fieldKey as keyof BusinessCard] !== null) {
      return String(savedOcrData[fieldKey as keyof BusinessCard]);
    }
    
    // スキャン結果から取得
    const ocrKey = Object.entries(ocrFieldMapping).find(([_, val]) => val === fieldKey)?.[0];
    if (ocrKey && scanResult && scanResult.data &&
        (scanResult.data as Record<string, any>)[ocrKey] !== undefined &&
        (scanResult.data as Record<string, any>)[ocrKey] !== null &&
        (scanResult.data as Record<string, any>)[ocrKey] !== "null") {
      return String((scanResult.data as Record<string, any>)[ocrKey]);
    }
    
    return null;
  };
  
  // フィールドに既存のAPI応答データがあるか確認
  const hasApiData = (fieldKey: string): boolean => {
    return getApiFieldValue(fieldKey) !== null;
  };

  // フィールドがAPI応答データまたは登録済みデータに存在するかチェック（値が空でも）
  const hasApiDataField = (fieldKey: string): boolean => {
    // 1. 重複データ（apiResponseData）をチェック
    if (apiResponseData) {
      const apiKey = Object.entries(apiFieldMapping).find(([_, value]) => value === fieldKey)?.[0];
      if (apiKey && apiKey in apiResponseData) {
        return true;
      }
    }

    // 2. 登録済みデータ（savedRegisteredData）をチェック
    if (savedRegisteredData && fieldKey in savedRegisteredData) {
      return true;
    }

    return false;
  };
  
  // 郵便番号から住所を検索する機能
  const fetchAddressFromPostalCode = async (postalCode: string) => {
    try {
      // Remove hyphen if present
      const cleanedCode = postalCode.replace('-', '');

      // Use the real API instead of mock data
      const addressResult = await businessCardAPI.getAddressByPostalCode(cleanedCode);

      if (addressResult.success && addressResult.address) {
        return addressResult.address;
      }

      return null;
    } catch (error) {
      return null;
    }
  };
  
  // Add this new function to extract just the prefecture part from an address
  const extractPrefecture = (address: string): string | null => {
    // Match common prefecture patterns
    const prefecturePatterns = [
      /(...都)/,     // Tokyo
      /(...府)/,     // Kyoto, Osaka
      /(...県)/,     // Other prefectures
      /(...道)/      // Hokkaido
    ];
    
    for (const pattern of prefecturePatterns) {
      const match = address.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };
  
  // フォームフィールドの検証
  const validateField = (field: string, value: any) => {
    let isValid = true;
    let message = '';

    switch (field) {
      case 'email':
        if (value && !emailRegex.test(value)) {
          isValid = false;
          message = 'メールアドレスの形式が正しくありません';
        }
        break;
      // postalCodeのバリデーションは海外住所対応のため削除
    }

    setFormValidity(prev => ({
      ...prev,
      [field]: { valid: isValid, message }
    }));

    return isValid;
  };
  
  // フィールド変更時のエラー状態クリアハンドラー
  const handleFieldChange = (fieldKey: string) => {
    // エラー状態をクリア
    if (formValidity[fieldKey]?.valid === false) {
      setFormValidity(prev => ({
        ...prev,
        [fieldKey]: { valid: true }
      }));
    }
  };

  // 電話番号とメールアドレスの相互依存バリデーション
  const validatePhoneOrEmail = () => {
    const allValues = form.getFieldsValue();
    const hasPhone = allValues.phone && allValues.phone.trim() !== '';
    const hasEmail = allValues.email && allValues.email.trim() !== '';

    if (!hasPhone && !hasEmail) {
      // 両方空の場合はエラー
      setFormValidity(prev => ({
        ...prev,
        phone: { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' },
        email: { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' }
      }));
    } else {
      // どちらかが入力されている場合はエラーをクリア
      setFormValidity(prev => ({
        ...prev,
        phone: { valid: true },
        email: { valid: true }
      }));
    }
  };

  // 電話番号変更時のハンドラー
  const handlePhoneChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleFieldChange('phone');
  };

  // 電話番号入力確定時のハンドラー
  const handlePhoneBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    validatePhoneOrEmail();
    // 入力されているすべてのフィールドで重複チェック
    checkDuplicateCustomer();
  };

  // Eメール変更時のハンドラー（TextAreaとInput両方に対応）
  const handleEmailChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleFieldChange('email');
    validateField('email', e.target.value);
  };

  // Eメール入力確定時のハンドラー
  const handleEmailBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // メールアドレスの形式チェック
    const emailValue = e.target.value;
    let isEmailValid = true;

    if (emailValue && emailValue.trim() !== '') {
      isEmailValid = validateField('email', emailValue);
    }

    // 電話番号とメールアドレスの相互依存バリデーション
    validatePhoneOrEmail();

    // 有効なメールアドレスの場合は重複チェックを実行（入力されているすべてのフィールドで）
    if (isEmailValid && emailValue) {
      checkDuplicateCustomer();
    }
  };
  
  // 郵便番号変更時のハンドラー
  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // バリデーションは海外住所対応のため削除
  };

  // 郵便番号入力確定時のハンドラー
  const handlePostalCodeBlur = async (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {

    // 手動入力モードの時だけ実行
    if (!isManualInput) return;

    const postalCode = e.target.value;

    if (postalCode) {
      const fullAddress = await fetchAddressFromPostalCode(postalCode);
      if (fullAddress) {
        // Get current address
        const currentAddress = form.getFieldValue('address') || '';

        if (!currentAddress) {
          // 住所が空の場合はそのまま自動入力
          form.setFieldsValue({ address: fullAddress });
          message.success('郵便番号から住所を取得しました');
        } else {
          // 住所が入力済みの場合は確認モーダルを表示
          setNewAddress(fullAddress);
          setPostalCodeConfirmVisible(true);
        }
      }
    }

  };
  
  const handleFieldSelectionChange = (field: string, value: SelectionType) => {
    console.log(`===== EditResultPage - フィールド選択変更: ${field} =====`);
    console.log('EditResultPage - 選択値:', value);

    // 選択から除外するフィールドは処理しない
    if (EXCLUDED_FROM_SELECTION.includes(field)) {
      console.log('EditResultPage - 除外フィールドのため処理スキップ');
      return;
    }

    // すでに選択されている場合は処理をスキップ
    if (fieldSelections[field] === value) {
      console.log('EditResultPage - すでに選択されているため処理をスキップ');
      return;
    }

    // 選択UI経由での変更であることを示すフラグを設定
    setIsSelectingFromUI(true);

    // 選択状態を更新
    setFieldSelections(prev => ({
      ...prev,
      [field]: value
    }));
    console.log('EditResultPage - フィールド選択状態を更新');

    // 選択に基づいてフォームの値を更新
    if (value === 'existing') {
      // API応答から既存データを選択した場合
      // フォームに値を設定しない（選択UIを消さないため）
      const apiValue = getApiFieldValue(field);
      console.log('EditResultPage - 既存データ取得:', apiValue);
      console.log('EditResultPage - 既存データ選択時はフォームに値を設定しません');
    } else if (value === 'new') {
      // 新規データを選択した場合
      const currentFormValue = form.getFieldValue(field as keyof BusinessCard);
      const existingValue = getApiFieldValue(field);

      // 現在のフォーム値と既存値を比較（フィールドタイプに応じた比較）
      const isFormValueSameAsExisting = currentFormValue !== null &&
        currentFormValue !== undefined &&
        currentFormValue !== '' &&
        existingValue !== null &&
        ([
          'postalCode',
          'phone',
          'mobile',
          'fax'
        ].includes(field)
          // 数値フィールドは数字のみで比較
          ? String(currentFormValue).replace(/\D/g, '') === existingValue.replace(/\D/g, '')
          // その他のフィールドは直接比較
          : String(currentFormValue) === existingValue
        );

      if (!isFormValueSameAsExisting && currentFormValue !== null && currentFormValue !== undefined && currentFormValue !== '') {
        // フォーム値が既存値と異なる場合は、ユーザーの編集を保持
        console.log('EditResultPage - ユーザー編集を保持、フォーム値を変更しません:', currentFormValue);
      } else {
        // フォーム値が既存値と同じ、または空の場合は、OCRデータを復元
        let newValue: string | undefined = undefined;

        // savedOcrDataから取得（ユーザーが手入力した最新の値）
        if (savedOcrData &&
            (savedOcrData as Record<string, any>)[field] !== undefined &&
            (savedOcrData as Record<string, any>)[field] !== null &&
            (savedOcrData as Record<string, any>)[field] !== "null") {
          newValue = String((savedOcrData as Record<string, any>)[field]);
          console.log('EditResultPage - OCRデータ取得(savedOcrData):', newValue);
        }
        // scanResult.dataから取得（元のOCRデータ）
        else {
          const ocrKey = Object.entries(ocrFieldMapping).find(([_, val]) => val === field)?.[0];
          console.log('EditResultPage - OCRキー:', ocrKey);

          if (ocrKey && scanResult && scanResult.data &&
              (scanResult.data as Record<string, any>)[ocrKey] !== undefined &&
              (scanResult.data as Record<string, any>)[ocrKey] !== null &&
              (scanResult.data as Record<string, any>)[ocrKey] !== "null") {
            newValue = String((scanResult.data as Record<string, any>)[ocrKey]);
            console.log('EditResultPage - OCRデータ取得(scanResult):', newValue);
          }
        }

        // 値を設定
        if (newValue !== undefined) {
          const updatedValue = { [field]: newValue } as any;
          form.setFieldsValue(updatedValue);
          console.log('EditResultPage - フォームに新規データを設定:', updatedValue);
        } else {
          // データがなければ空にする
          console.log('EditResultPage - データなし、フィールドをクリア');
          form.setFieldValue(field as any, undefined);
        }
      }
    }

    console.log('==========================================');

    // フィールド選択後、重複チェックを実行する（選択確定後のタイミングで）
    // ただし本登録モード（仮登録顧客一覧からの本登録）では、既存重複は
    // selectTemporaryCustomer で確定済みのため再検索しない。
    // 再検索するとフォームの現在値（例: 仮登録の空メール）で照合され、
    // 「重複なし」と判定された場合に apiResponse の duplicatelist を破棄してしまい、
    // 既存データの「新規/既存」トグルが消える不具合が起きるため抑止する。
    if (!isPermanentMode && (field === 'name' || field === 'phone' || field === 'email')) {
      console.log('EditResultPage - フィールド選択変更、200ms後に重複チェック実行');
      setTimeout(() => checkDuplicateCustomer(), 200);
    }

    // 選択UI経由フラグをリセット（非同期処理後にリセット）
    setTimeout(() => setIsSelectingFromUI(false), 100);
  };
  
  // 重複確認を実行する共通関数
  // 入力されているすべてのフィールドで重複チェックを行う
  const checkDuplicateCustomer = async () => {
    // 仮登録の本登録モードでは、重複情報は select/regist のレスポンスから取得済みのため
    // クライアント側の重複チェックは行わない。
    // （初期化時に仕掛けた setTimeout が、前の顧客の scanResult を捕捉したクロージャで
    //   発火し、setScanResult で画像だけ前の顧客に戻ってしまう不具合を防ぐ）
    if (isPermanentMode) return;
    try {
      const token = await indexedDBStorage.getItem('token');
      if (!token) return;

      // フォームの現在の値を取得
      const currentValues = form.getFieldsValue(true);
      console.log('===== EditResultPage - 重複チェック開始 =====');
      console.log('EditResultPage - 現在のフォーム値:', currentValues);
      console.log('EditResultPage - 編集対象顧客番号:', editingCustomerNo);
      console.log('EditResultPage - 重複チェック除外対象顧客番号:', excludeFromDuplicateCheckCustomerNo);

      // 入力されているフィールドのみを含めてデータを構築
      const data: any = {
        customerdata: {}
      };

      // 既存顧客編集中の場合のみcustomernoを含める（自分自身を重複リストから除外するため）
      if (excludeFromDuplicateCheckCustomerNo) {
        data.customerdata.customerno = excludeFromDuplicateCheckCustomerNo;
      }

      // 入力されているフィールドをすべて含める
      if (currentValues.name && currentValues.name.trim() !== '') {
        data.customerdata.customername = currentValues.name;
      }
      if (currentValues.phone && currentValues.phone.trim() !== '') {
        data.customerdata.tel = currentValues.phone;
      }
      if (currentValues.email && currentValues.email.trim() !== '') {
        data.customerdata.mailaddress = currentValues.email;
      }

      console.log('EditResultPage - 重複チェックリクエストデータ:', data);

      // いずれかのフィールドが入力されている場合のみAPIを呼び出す
      const hasValue = data.customerdata.customername ||
                       data.customerdata.tel ||
                       data.customerdata.mailaddress;

      if (hasValue) {
        const response = await businessCardAPI.checkDuplicate(data.customerdata);
        console.log('EditResultPage - 重複チェックレスポンス:', response);
        console.log('EditResultPage - 重複リスト:', response.duplicatelist);
        
        // duplicatelistが存在し、空配列でない場合
        if (response.duplicatelist && response.duplicatelist.length > 0) {
          console.log('EditResultPage - 重複データ検出:', response.duplicatelist.length, '件');
          // scanResultがある場合、apiResponseを更新
          if (scanResult) {
            // scanResultを複製
            const updatedScanResult = { ...scanResult };

            // apiResponseが存在しない場合は作成
            if (!updatedScanResult.apiResponse) {
              updatedScanResult.apiResponse = {};
            }

            // duplicatelistを更新
            updatedScanResult.apiResponse.duplicatelist = response.duplicatelist;

            // 重複したデータの最初の顧客を取得
            const firstDuplicate = response.duplicatelist[0];
            console.log('EditResultPage - 最初の重複データ:', firstDuplicate);
            if (firstDuplicate && firstDuplicate.customerno) {
              console.log('EditResultPage - 重複顧客番号:', firstDuplicate.customerno);
              // 重複データを更新（editingCustomerNoは変更しない）

              // duplicatedataを更新
              updatedScanResult.apiResponse.duplicatedata = firstDuplicate;
              console.log('EditResultPage - duplicatedataを更新:', firstDuplicate);

              // APIレスポンスデータも更新
              setApiResponseData(firstDuplicate);
              console.log('EditResultPage - apiResponseDataを更新');

              // optionfieldを解析してフリガナの既存値を設定
              if (firstDuplicate.optionfield) {
                try {
                  const optionField = JSON.parse(firstDuplicate.optionfield);
                  console.log('EditResultPage - optionfield(parsed):', optionField);
                  // furiganaフィールドが存在する場合（値が空でも）設定
                  if ('furigana' in optionField) {
                    const furiganaVal = optionField.furigana || '';
                    console.log('EditResultPage - 既存furigana値検出（重複チェック時）:', furiganaVal);
                    setFuriganaExistingValue(furiganaVal);
                    // 重複チェック時は選択状態を変更しない（新規側が選択されたまま）
                    console.log('EditResultPage - フリガナ選択状態は変更しません');
                  }
                } catch (error) {
                  console.error('EditResultPage - optionfield解析エラー（重複チェック時）:', error);
                }
              }
            }

            // 更新したscanResultを保存
            setScanResult(updatedScanResult);
            await indexedDBStorage.setItem('scanResult', JSON.stringify(updatedScanResult));
            console.log('EditResultPage - scanResultを保存完了');

            // 画面を再処理するためのメッセージ
            message.info('重複する顧客情報が見つかりました');
          }
        } else {
          console.log('EditResultPage - 重複データなし');

          // 重複がない場合は apiResponseData をクリア
          setApiResponseData(null);

          // 重複チェックの結果、重複がなくなったことを記録
          // editingCustomerNoは変更しない（既存顧客編集中の場合はそのまま維持）
          console.log('EditResultPage - 重複データクリア、editingCustomerNoは維持:', editingCustomerNo);

          // フリガナの既存値もクリア（「新規/既存」選択UIを非表示にするため）
          setFuriganaExistingValue('');
          setFuriganaSelection('new');
          console.log('EditResultPage - フリガナの既存値をクリア');

          // scanResultのduplicatelistも空配列に更新
          if (scanResult) {
            const updatedScanResult = { ...scanResult };

            if (!updatedScanResult.apiResponse) {
              updatedScanResult.apiResponse = {};
            }

            updatedScanResult.apiResponse.duplicatelist = [];
            updatedScanResult.apiResponse.duplicatedata = null;

            setScanResult(updatedScanResult);
            await indexedDBStorage.setItem('scanResult', JSON.stringify(updatedScanResult));
            console.log('EditResultPage - 重複データをクリアしました');
          }
        }
        console.log('==========================================');
      }
    } catch (error) {
      console.error('重複確認API呼び出しエラー:', error);
    }
  };
  
  // 通報モーダルを開く
  const openReportModal = () => {
    setReportModalVisible(true);
  };
  
  // 通報を送信
  const submitReport = () => {
    if (!reportContent.trim()) {
      message.error('通報内容を入力してください');
      return;
    }
    
    // 通報内容を送信する処理（実際はAPIコール）
    message.success('AIへの通報が送信されました');
    setReportModalVisible(false);
    setReportContent('');
  };
  
  // 一時画像削除用関数
  const deleteTemporaryImage = async () => {
    const stored = await indexedDBStorage.getItem('scanResult');
    if (!stored) return;
    try {
      const parsed: ScanResult = JSON.parse(stored);
      const filename = parsed.imageUrl;
      if (filename) {
        await axios.post(API_PATHS.DELETE_IMAGE, { filename }, {
          headers: { 'Content-Type': 'application/json' }
        });
      }
    } catch (error) {
    }
    //await indexedDBStorage.removeItem('scanResult');
  };
  
  // 「戻る」ボタンの処理（削除してカメラへ）
  const handleBack = async () => {
    // 本登録モードの場合は仮登録顧客一覧へ戻す（一覧モーダル自動再オープン）
    const permFlag = await indexedDBStorage.getItem('permanentRegistrationMode');
    if (permFlag === 'true') {
      await handleReturnToTempCustomerList();
      return;
    }

    await deleteTemporaryImage();

    // 裏面撮影モードフラグを削除（通常の撮影モードに戻す）
    await indexedDBStorage.removeItem('backSideCaptureMode');

    // 手動入力モードかチェック
    const isManualInput = await indexedDBStorage.getItem('isManualInput');
    if (isManualInput === 'true') {
      // 手動入力モードの場合はメニューに戻る
      await indexedDBStorage.removeItem('isManualInput');
      navigate(getRoutePath('/menu'));
      return;
    }

    // 常にFlutterCamera.postMessageを使ってカメラを起動（WebView遷移なし）
    if (isInFlutter && window.FlutterCamera) {
      window.FlutterCamera.postMessage('openCamera');
    } else {
      navigate(getRoutePath('/camera'));
    }
  };
  
  // 「再撮影」ボタンの処理
  const handleRetake = async () => {
    await deleteTemporaryImage();

    // 裏面撮影モードフラグを削除（通常の撮影モードに戻す）
    await indexedDBStorage.removeItem('backSideCaptureMode');

    // 常にFlutterCamera.postMessageを使ってカメラを起動（WebView遷移なし）
    if (isInFlutter && window.FlutterCamera) {
      window.FlutterCamera.postMessage('openCamera');
    } else {
      navigate(getRoutePath('/camera'));
    }
  };

  // 裏面取り込みハンドラー
  const handleBackSideCapture = async () => {
    try {
      // 現在のフォーム値を保存
      const currentFormValues = form.getFieldsValue();
      await indexedDBStorage.setItem('tempFormValues', JSON.stringify(currentFormValues));

      // フィールド選択状態も保存
      await indexedDBStorage.setItem('tempFieldSelections', JSON.stringify(fieldSelections));

      // 裏面撮影モードフラグをIndexedDBに保存（念のため削除してから設定）
      await indexedDBStorage.removeItem('backSideCaptureMode');
      await indexedDBStorage.setItem('backSideCaptureMode', 'true');

      // Flutter環境ならネイティブカメラを起動、Web環境ならCameraPageに遷移
      if (isInFlutter && window.FlutterCamera) {
        window.FlutterCamera.postMessage('openCamera');
      } else {
        navigate(getRoutePath('/camera'));
      }
    } catch (error) {
      console.error('EditResultPage - 裏面取り込みエラー:', error);
      message.error('裏面取り込みの準備に失敗しました');
    }
  };

  // フォームの値が変更されたときのハンドラー
  const handleFormValuesChange = (changedValues: any, allValues: BusinessCard) => {
    // 変更されたフィールドのエラー状態をクリア
    const updatedValidity = { ...formValidity };
    let hasChanges = false;

    Object.keys(changedValues).forEach(fieldKey => {
      if (formValidity[fieldKey]?.valid === false) {
        updatedValidity[fieldKey] = { valid: true };
        hasChanges = true;
      }
    });

    // 電話番号またはメールアドレスが変更された場合、両方のエラーをクリア
    if (changedValues.phone !== undefined || changedValues.email !== undefined) {
      if (formValidity['phone']?.valid === false || formValidity['email']?.valid === false) {
        updatedValidity['phone'] = { valid: true };
        updatedValidity['email'] = { valid: true };
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setFormValidity(updatedValidity);
    }

    // name/phone/emailのいずれかが変更された場合、条件付きで重複チェックを実行
    if (changedValues.name !== undefined ||
        changedValues.phone !== undefined ||
        changedValues.email !== undefined) {

      // 変更されたフィールドのキーを取得
      const fieldKey = Object.keys(changedValues).find(key =>
        key === 'name' || key === 'phone' || key === 'email'
      );

      if (fieldKey) {
        const currentValue = allValues[fieldKey as keyof BusinessCard];
        const apiData = getApiFieldValue(fieldKey);

        console.log('EditResultPage - フィールド変更検知:', { fieldKey, currentValue, apiData });

        // 通常: 重複データがあり、かつ入力値がその値と異なる場合のみ重複チェックを実行。
        // 緊急モードの初回登録: 既存データが無くても電話等の編集で毎回重複チェックする
        //（全く新規→電話編集で重複が見つかった場合に、モーダル＋全ロックへ遷移させるため）
        if ((apiData !== null && String(currentValue) !== String(apiData))
            || (emergencyMode && isInitialRegistration)) {
          console.log('EditResultPage - 重複チェックを実行します（通常差分 or 緊急モード初回登録）');

          // 変更されたフィールドの選択状態を「new」に設定
          setFieldSelections(prev => ({
            ...prev,
            [fieldKey]: 'new'
          }));
          console.log('EditResultPage - フィールド選択を「new」に設定:', fieldKey);

          // 既存のタイマーをクリア
          if (duplicateCheckTimerRef.current) {
            clearTimeout(duplicateCheckTimerRef.current);
          }

          // debounceで重複チェックを実行（200ms後）
          duplicateCheckTimerRef.current = setTimeout(() => {
            console.log('EditResultPage - debounce後、重複チェックを実行します');

            // メールアドレスが変更された場合、バリデーションチェック
            if (changedValues.email !== undefined) {
              const emailValue = allValues.email;
              if (emailValue && emailValue.trim() !== '') {
                const isEmailValid = emailRegex.test(emailValue);
                if (!isEmailValid) {
                  console.log('EditResultPage - メールアドレスの形式が不正なため、重複チェックをスキップします');
                  return;
                }
              }
            }

            // 重複チェック実行
            checkDuplicateCustomer();
          }, 200);
        } else {
          console.log('EditResultPage - 重複データがないか、入力値が同じため、重複チェックをスキップします');
        }
      }
    }

    // ユーザーが手入力で値を変更した場合、savedOcrDataに保存
    // （選択UI経由での変更は除外）
    if (!isSelectingFromUI && Object.keys(changedValues).length > 0) {
      setSavedOcrData(prev => ({
        ...prev,
        ...changedValues
      }));
      console.log('EditResultPage - savedOcrDataを更新:', changedValues);
    }
  };

  // selectedContactUsersが変更されたときに実行するuseEffect
  useEffect(() => {
    // 担当者一覧から担当者名を取得して設定
    if (selectedContactUsers.length > 0) {
      const representativeNames = selectedContactUsers
        .map(userno => {
          // sys ユーザー（userno が負）は「システム管理者」として表示
          if (userno < 0) return 'システム管理者';

          // まず現在の担当者リストから探す
          let user = contactUsers.find(u => u.userno === userno);
          let isRetired = false;
          let isDeleted = false;
          
          // 現在の担当者リストにない場合、退職者リストから探す
          if (!user) {
            user = retiredUsers.find(u => u.userno === userno);
            isRetired = true;
          }
          
          // どちらのリストにもない場合は削除済みユーザーとして扱う
          if (!user) {
            isDeleted = true;
            return `(削除済ユーザ)`;
          }
          
          return isRetired ? `(退職者)${user.username}` : user.username;
        })
        .filter(name => name !== null)
        .join(', ');
      
      if (representativeNames) {
        // フォームの担当者フィールドを更新
        form.setFieldsValue({ representative: representativeNames });
      }
    }
  }, [selectedContactUsers, contactUsers, retiredUsers, form]);

  // 重複チェック結果が変化したとき → 担当者IDリストをセット/リセット
  useEffect(() => {
    // 仮登録の本登録モードでは担当者は仮登録レコード（アップロード時に指定した担当者）を採用するため、
    // ここでの重複/ログインユーザーによる上書きは行わない（専用の useEffect で設定する）。
    if (isPermanentMode) return;

    const existingIds = getApiContactUserIds();

    if (existingIds && existingIds.length > 0) {
      // 重複顧客が見つかった場合：既存顧客の担当者 ＋ ログインユーザー
      const merged = [...existingIds];
      if (user?.id && !merged.includes(user.id)) {
        merged.push(user.id);
      }
      setSelectedContactUsers(Array.from(new Set(merged)));
      console.log('EditResultPage - 担当者をセット:', { existingIds, merged });
    } else if (apiResponseData === null) {
      // editingCustomerNoの顧客は重複チェックから除外されているためその有無で判定する
      if (editingCustomerNo) {
        // 登録済み顧客 → 登録済み担当者に戻す（なければログインユーザーのみ）
        const registeredIds = extractContactUserIds(
          scanResult?.apiResponse?.customerdata ||
          scanResult?.apiResponse?.duplicatedata
        );
        const resetIds = registeredIds.length > 0 ? registeredIds : (user?.id ? [user.id] : []);
        setSelectedContactUsers(resetIds);
        console.log('EditResultPage - 担当者を登録済みにリセット:', resetIds);
      } else {
        // 新規顧客 → ログインユーザーのみ
        if (user?.id) {
          setSelectedContactUsers([user.id]);
          console.log('EditResultPage - 担当者をログインユーザーのみにリセット:', user.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponseData, isPermanentMode]);

  // 仮登録の本登録モードでは、担当者は仮登録レコード（＝アップロード時に指定した担当者）を採用する。
  // 重複の有無やログインユーザーで上書きしない。scanResult 読み込み後・次の顧客への切替後にも追従する。
  useEffect(() => {
    if (!isPermanentMode) return;
    const cd: any = scanResult?.apiResponse?.customerdata;
    if (!cd) return; // scanResult 読み込み前は何もしない（読み込み後に再実行される）
    // 既存（本登録済み）顧客がある場合は、既存側(duplicatedata)の担当者をそのまま採用し、
    // ログインユーザーが含まれていなければ追加する（②既存側担当者の表示）。
    const dd: any =
      (scanResult?.apiResponse as any)?.duplicatedata ||
      (scanResult?.apiResponse as any)?.duplicatelist?.[0] ||
      null;
    // アップロード時（仮登録）の担当者を算出。contact系が空なら名刺登録者(businesscarduploaduserno)で代替。
    let uploadIds = extractContactUserIds(cd);
    if (uploadIds.length === 0) {
      const uploadUserno = Number(cd.businesscarduploaduserno);
      if (!Number.isNaN(uploadUserno) && uploadUserno > 0) {
        uploadIds = [uploadUserno];
      }
    }
    if (dd) {
      // 既存（本登録済み）顧客の担当者は contactUserList に入る。contactusernoArray が空("[]")でも
      // 取りこぼさないよう、配列→リスト→単体の順で「空でなければ採用」する（共有関数は変更しない）。
      const arrIds = parseContactUserIds(dd.contactusernoArray);
      const listIds = Array.isArray(dd.contactUserList)
        ? dd.contactUserList.map((u: any) => Number(u?.userno)).filter((n: number) => !Number.isNaN(n))
        : [];
      const existingIds = arrIds.length > 0 ? arrIds : (listIds.length > 0 ? listIds : parseContactUserIds(dd.contactuserno));
      // 既存側担当者 ＋ アップロード時担当者 を統合（重複排除）。ログインユーザーが居なければ追加。
      const merged = [...existingIds, ...uploadIds];
      if (user?.id && !merged.includes(user.id)) {
        merged.push(user.id);
      }
      setSelectedContactUsers(merged.length > 0 ? Array.from(new Set(merged)) : (user?.id ? [user.id] : []));
      return;
    }
    setSelectedContactUsers(uploadIds.length > 0 ? uploadIds : (user?.id ? [user.id] : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermanentMode, scanResult, user]);

  // Flutter環境でのprocessImageイベント監視
  useEffect(() => {
    if (!isInFlutter) return;

    const handleProcessImage = async (event: CustomEvent) => {
      console.log('EditResultPage - processImageイベント受信');
      const imageData = event.detail?.imageData;

      if (!imageData) {
        console.error('EditResultPage - 画像データが見つかりません');
        return;
      }

      // 通信環境が悪い場合に「画像を処理中...」のまま固まらないよう OCR に30秒のタイムアウトを設ける
      const controller = new AbortController();
      let didTimeout = false;
      const timeoutId = setTimeout(() => { didTimeout = true; controller.abort(); }, 30000);
      try {
        setLoading(true);
        message.loading('画像を処理中...', 0);

        // 裏面撮影モードかチェック
        const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');

        if (backSideCaptureMode === 'true') {
          // 裏面撮影モード：OCRをスキップして画像だけ保存
          console.log('EditResultPage - 裏面撮影モード: 画像を保存');

          // 裏面画像をscanResultに保存
          const currentScanResult = scanResult;
          if (currentScanResult && currentScanResult.data) {
            currentScanResult.data.businesscardbackimage = imageData;
            setScanResult({...currentScanResult});
            setBackSideImage(imageData);

            // IndexedDBに保存
            await indexedDBStorage.setItem('scanResult', JSON.stringify(currentScanResult));
            await indexedDBStorage.setItem('editResultBackup', JSON.stringify(currentScanResult));
          }

          // 裏面撮影モードフラグを削除
          await indexedDBStorage.removeItem('backSideCaptureMode');

          // 一時保存されたフォーム値を復元
          const tempFormValuesJson = await indexedDBStorage.getItem('tempFormValues');
          if (tempFormValuesJson) {
            const tempFormValues = JSON.parse(tempFormValuesJson);
            form.setFieldsValue(tempFormValues);
            await indexedDBStorage.removeItem('tempFormValues');
          }

          // フィールド選択状態を復元
          const tempFieldSelectionsJson = await indexedDBStorage.getItem('tempFieldSelections');
          if (tempFieldSelectionsJson) {
            const tempFieldSelections = JSON.parse(tempFieldSelectionsJson);
            setFieldSelections(tempFieldSelections);
            await indexedDBStorage.removeItem('tempFieldSelections');
          }

          message.destroy();
          message.success('裏面画像を保存しました');
        } else {
          // 通常モード：OCR処理を実行
          console.log('EditResultPage - 通常モード: OCR処理を実行');

          // OCR APIを呼び出し
          const token = await indexedDBStorage.getItem('token');
          if (!token) {
            throw new Error('認証トークンが見つかりません');
          }

          // ユーザー情報から担当者番号を取得
          const userDataStr = await indexedDBStorage.getItem('user');
          let contactuserno = [];

          if (userDataStr) {
            try {
              const userData = JSON.parse(userDataStr);
              if (userData && userData.userno) {
                contactuserno.push(userData.userno);
              }
            } catch (e) {
              console.error('ユーザー情報の解析に失敗:', e);
            }
          }

          // 設定モーダルで保存された AI解析モードを取得し、最後に使用したモードとして記録
          const currentOcrModel = await getOcrModelPreference();
          await setLastOcrUsedModel(currentOcrModel);

          // 撮影時に確定した保持値を使用（解析エンジンをフロー内で一致させる）
          const useChatGpt = await getEmergencyMode();
          // OCR APIを呼び出し
          const ocrResponse = await axios.post(API_PATHS.PROCESS_OCR, {
            filename: `businesscard_${Date.now()}.jpg`,
            base64: imageData,
            contactuserno: JSON.stringify(contactuserno),
            ocr_model: currentOcrModel,
            use_chatgpt: useChatGpt // true のとき ChatGPT 用キーで解析
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });

          console.log('EditResultPage - OCR結果:', ocrResponse.data);

          // OCRデータをビジネスカードデータ形式に変換
          const convertedData: Partial<BusinessCard> = {};

          if (ocrResponse.data && ocrResponse.data.ocrdata) {
            Object.entries(ocrResponse.data.ocrdata).forEach(([key, value]) => {
              const mappedKey = ocrFieldMapping[key];
              if (mappedKey && value !== null && value !== undefined && value !== "null") {
                (convertedData as any)[mappedKey] = String(value);
              }
            });
          }

          // 表面画像を設定
          convertedData.businesscardfrontimage = imageData;

          // 名刺登録日がない場合は現在日時を設定
          if (!convertedData.businesscardcreatetime) {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            convertedData.businesscardcreatetime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          }

          // 新しいスキャン結果を作成
          const newScanResult: ScanResult = {
            success: true,
            data: convertedData,
            apiResponse: ocrResponse.data
          };

          // 状態を更新
          setScanResult(newScanResult);
          setSavedOcrData(convertedData);

          // フォームに新しいデータを設定
          form.setFieldsValue(convertedData);

          // 顧客番号の更新（新規顧客の場合は何も設定しない）
          // OCRレスポンスに customerdata.customerno がある場合は既存顧客更新の結果
          if (ocrResponse.data.customerdata && ocrResponse.data.customerdata.customerno) {
            console.log('EditResultPage - OCRレスポンスに顧客番号あり（既存更新）:', ocrResponse.data.customerdata.customerno);
            setEditingCustomerNo(ocrResponse.data.customerdata.customerno);
            setExcludeFromDuplicateCheckCustomerNo(ocrResponse.data.customerdata.customerno);
            await indexedDBStorage.setItem('editingCustomerNo', String(ocrResponse.data.customerdata.customerno));
            await indexedDBStorage.setItem('excludeFromDuplicateCheckCustomerNo', String(ocrResponse.data.customerdata.customerno));
          }
          // duplicatedata.customerno は重複チェック結果なので、editingCustomerNoには設定しない

          // 担当者情報を更新
          if (ocrResponse.data.duplicatedata && ocrResponse.data.duplicatedata.contactuserno) {
            try {
              let contactUserIds: number[] = [];
              const contactUsernoValue = ocrResponse.data.duplicatedata.contactuserno;

              if (typeof contactUsernoValue === 'string') {
                const parsed = JSON.parse(contactUsernoValue);
                contactUserIds = Array.isArray(parsed) ? parsed.map(Number) : [Number(parsed)];
              } else if (Array.isArray(contactUsernoValue)) {
                contactUserIds = contactUsernoValue.map(Number);
              } else if (typeof contactUsernoValue === 'number') {
                contactUserIds = [contactUsernoValue];
              }

              setSelectedContactUsers(contactUserIds);
            } catch (e) {
              console.error('EditResultPage - 担当者情報の解析に失敗:', e);
            }
          }

          // IndexedDBに保存
          await indexedDBStorage.setItem('scanResult', JSON.stringify(newScanResult));
          await indexedDBStorage.setItem('editResultBackup', JSON.stringify(newScanResult));

          // フィールド選択をリセット（すべて新規データを選択）
          setFieldSelections({});

          message.destroy();
          message.success('画像の処理が完了しました');
        }

        clearTimeout(timeoutId);
        setLoading(false);
      } catch (error: any) {
        clearTimeout(timeoutId);
        console.error('EditResultPage - processImage error:', error);
        message.destroy();
        if (didTimeout || error?.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error))) {
          message.error('通信がタイムアウトしました。通信環境をご確認のうえ、もう一度撮影してください。');
        } else {
          message.error(error.message || '画像の処理に失敗しました');
        }
        setLoading(false);
      }
    };

    const handleProcessImageError = (event: CustomEvent) => {
      if (event.detail && event.detail.error) {
        message.error(event.detail.error);
      }
    };

    window.addEventListener('processImage', handleProcessImage as unknown as EventListener);
    window.addEventListener('processImageError', handleProcessImageError as unknown as EventListener);

    return () => {
      window.removeEventListener('processImage', handleProcessImage as unknown as EventListener);
      window.removeEventListener('processImageError', handleProcessImageError as unknown as EventListener);
    };
  }, [isInFlutter, form, scanResult]);

  // 担当者一覧の表示用関数
  const renderContactUsers = () => {
    if (selectedContactUsers.length === 0) {
      return (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center' }}>
          <Text type="secondary">担当者が設定されていません</Text>
        </div>
      );
    }

    // 担当者リスト表示部分
    return (
      <div>
        {/* 既存担当者のリスト表示 */}
        <div style={{ marginBottom: selectedContactUsers.length > 0 ? '8px' : 0 }}>
          {selectedContactUsers.map((userno) => {
            // sys ユーザー（userno が負）は「システム管理者」として通常スタイルで表示
            const isSystemUser = userno < 0;
            // まず現在の担当者リストから探す
            let user = isSystemUser ? undefined : contactUsers.find(u => u.userno === userno);
            let isRetired = false;
            let isDeleted = false;

            // 現在の担当者リストにない場合、退職者リストから探す
            if (!user && !isSystemUser) {
              user = retiredUsers.find(u => u.userno === userno);
              isRetired = !!user;
            }

            // どちらのリストにもない場合は削除済みユーザーとして扱う（sys は除外）
            if (!user && !isSystemUser) {
              isDeleted = true;
            }
            
            return (
              <Tag 
                key={userno}
                style={{ 
                  padding: '4px 8px', 
                  marginBottom: '6px', 
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: isRetired || isDeleted ? '#ff4d4f' : undefined,
                  backgroundColor: isRetired || isDeleted ? '#fff2f0' : undefined,
                  borderColor: isRetired || isDeleted ? '#ffccc7' : undefined
                }}
                closable={selectedContactUsers.length > 1 && canEditContactUser}
                onClose={(e) => {
                  e.preventDefault();
                  // 担当者を削除（複数担当者がある場合のみ）
                  if (selectedContactUsers.length > 1 && canEditContactUser) {
                    setSelectedContactUsers(prev => prev.filter(id => id !== userno));
                  }
                }}
              >
                {userno < 0
                  ? 'システム管理者'
                  : isDeleted
                    ? `(削除済ユーザ)`
                    : user
                      ? (isRetired ? `(退職者)${user.username}` : user.username)
                      : `(削除済ユーザ)`
                }
              </Tag>
            );
          })}
        </div>

        {/* 担当者追加ドロップダウン（担当者編集権限がある場合のみ表示） */}
        {canEditContactUser && (
          !addingContactUser ? (
            <Button
              type="dashed"
              size="small"
              onClick={() => setAddingContactUser(true)}
              style={{ marginTop: '6px' }}
            >
              + 担当者を追加
            </Button>
          ) : (
            <div style={{ marginTop: '8px' }}>
              <Row gutter={8}>
                <Col span={16}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="追加する担当者を選択"
                    onChange={(value) => {
                      if (value && !selectedContactUsers.includes(Number(value))) {
                        // 選択された担当者を追加
                        setSelectedContactUsers(prev => [...prev, Number(value)]);
                      }
                      setAddingContactUser(false);
                    }}
                    onBlur={() => setAddingContactUser(false)}
                  >
                    {contactUsers
                      .filter(user => !selectedContactUsers.includes(user.userno))
                      .map(user => (
                        <Select.Option key={user.userno} value={user.userno}>
                          {user.username} ({user.deptname})
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    onClick={() => setAddingContactUser(false)}
                    size="middle"
                    style={{ width: '100%', padding: '0 8px' }}
                  >
                    キャンセル
                  </Button>
                </Col>
              </Row>
            </div>
          )
        )}
      </div>
    );
  };

  // 高品質モードで再解析
  const handleRescan = async () => {
    if (!scanResult?.imageUrl) return;
    // 再アップロードで上書きされる旧一時画像のファイル名を退避（成功後に削除する）
    const oldImageUrl = scanResult.imageUrl;
    // 通信環境が悪い場合に「再解析中...」のまま固まらないよう、再アップロード〜OCR の
    // 一連の通信に合計30秒のタイムアウトを設ける。キャンセルボタンからも中断できる。
    const controller = new AbortController();
    rescanAbortRef.current = controller;
    rescanCancelledRef.current = false;
    let didTimeout = false;
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, 30000);
    setRescanning(true);
    try {
      // 高品質モードで再解析した旨を記録（UI 表示の切替に使用）
      await setLastOcrUsedModel('high');
      setLastOcrModel('high');
      // 既存のBase64画像を取得
      const base64 = scanResult.data?.businesscardfrontimage;
      if (!base64) throw new Error('画像データがありません');

      // Base64をBlobに変換
      const byteString = atob(base64.split(',')[1]);
      const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: mimeString });

      // 画像を再アップロード
      const formData = new FormData();
      formData.append('image', blob, 'rescan_image.jpg');
      formData.append('action', 'upload');
      const uploadResp = await axios.post(API_PATHS.UPLOAD_IMAGE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal
      });
      if (!uploadResp.data.success) {
        // サーバーから返された詳細なデバッグ情報をログに出力
        if (uploadResp.data.debug) {
          console.error('Upload failed - Debug info:', uploadResp.data.debug);
          window.FlutterDebug?.postMessage('Upload debug: ' + JSON.stringify(uploadResp.data.debug));
        }
        throw new Error(uploadResp.data.message || 'アップロードに失敗しました');
      }
      const filename: string = uploadResp.data.filename;

      // OCR API 呼び出し
      const token = await indexedDBStorage.getItem('token');
      const useChatGpt = await getEmergencyMode();
      const ocrResp = await axios.post(API_PATHS.PROCESS_OCR, { filename, base64, ocr_model: 'high', use_chatgpt: useChatGpt }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      if (!ocrResp.data.success) {
        // サーバーから返された詳細なデバッグ情報をログに出力
        if (ocrResp.data.debug) {
          console.error('OCR failed - Debug info:', ocrResp.data.debug);
          window.FlutterDebug?.postMessage('OCR debug: ' + JSON.stringify(ocrResp.data.debug));
        }
        throw new Error(ocrResp.data.message || '再読み取りに失敗しました');
      }

      // ここで OCR は成功。以降はローカル状態の反映のみで中断できないため、
      // 「成功直後のキャンセル」による二重処理・結果不整合を防ぐべく中断を無効化する。
      clearTimeout(timeoutId);
      rescanAbortRef.current = null;

      // 新しいスキャン結果を構築（scanResult.data は日本語キーのまま保持）
      const newResult: ScanResult = {
        success: true,
        data: ocrResp.data.data,
        imageUrl: filename,
        apiResponse: ocrResp.data.apiResponse
      };
      // Base64画像を保持
      newResult.data!.businesscardfrontimage = base64;
      setScanResult(newResult);
      await indexedDBStorage.setItem('scanResult', JSON.stringify(newResult));
      await indexedDBStorage.setItem('editResultBackup', JSON.stringify(newResult));

      // 旧一時画像を削除（scanResult が新ファイル名に切り替わった後に行う）。
      // best-effort のクリーンアップなので await せず fire-and-forget にする。
      // await すると削除 API が無応答のとき成功フローが止まり「再解析中...」が
      // 固まるため。失敗しても再読み取りは成功として進める（孤児はサーバー側バッチで回収）。
      if (oldImageUrl && !oldImageUrl.startsWith('data:') && oldImageUrl !== filename) {
        axios.post(API_PATHS.DELETE_IMAGE, { filename: oldImageUrl }, {
          headers: { 'Content-Type': 'application/json' }
        }).catch(() => {
          // 削除失敗は無視（孤児はサーバー側バッチで回収）
        });
      }

      // OCRデータ（日本語キー）を BusinessCard 形式（英語キー）へ変換してフォームに反映
      const convertedData: Partial<BusinessCard> = {};
      if (newResult.data) {
        Object.entries(newResult.data as Record<string, any>).forEach(([key, value]) => {
          const mappedKey = ocrFieldMapping[key];
          if (mappedKey && value !== null && value !== undefined && value !== "null") {
            (convertedData as any)[mappedKey] = String(value);
          }
        });
      }
      form.setFieldsValue(convertedData);
      setSavedOcrData(convertedData);
      // 既存/新規の選択状態をリセット（新しい OCR 結果がフォームに反映されるようにする）
      setFieldSelections({});

      message.success('再読み取りが完了しました');
    } catch (error: any) {
      if (rescanCancelledRef.current) {
        // ユーザーがキャンセルした場合はエラー表示せず、そのまま編集画面に留まる
        message.info('再解析をキャンセルしました');
      } else if (didTimeout || error?.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error))) {
        // タイムアウト（合計30秒超過）。画像は残っているので再試行を促す。
        message.error('通信がタイムアウトしました。通信環境をご確認のうえ、もう一度お試しください。');
      } else {
        logApiError('EditResultPage.handleRescan', error, API_PATHS.PROCESS_OCR, 'POST');
        message.error(error.message || '再読み取りに失敗しました');
      }
    } finally {
      clearTimeout(timeoutId);
      rescanAbortRef.current = null;
      rescanCancelledRef.current = false;
      setRescanning(false);
    }
  };

  // 再解析のキャンセル（進行中の通信を中断して編集画面に戻す）
  const handleCancelRescan = () => {
    // OCR 成功後（rescanAbortRef が null）は中断できない。押しても何もしない。
    if (!rescanAbortRef.current) return;
    rescanCancelledRef.current = true;
    rescanAbortRef.current?.abort();
  };
  
  // ビジネスカードデータをAPIリクエスト形式に変換する関数
  const convertToApiRequest = async (formData: BusinessCard): Promise<Record<string, any>> => {
    console.log('===== EditResultPage - API登録データ変換開始 =====');
    console.log('EditResultPage - フォームデータ:', formData);
    console.log('EditResultPage - 編集対象顧客番号:', editingCustomerNo);

    const apiData: Record<string, any> = {};

    // 顧客番号の設定はsubmitToApi内で行うため、ここでは設定しない
    
    // 各フィールドをAPIのフィールド名に変換してデータを設定
    Object.entries(formData).forEach(([key, value]) => {
      const apiKey = apiRequestMapping[key as keyof BusinessCard];
      if (apiKey) {
        // fieldSelectionsで既存が選択されている場合はapiResponseDataから取得
        if (fieldSelections[key] === 'existing') {
          const apiValue = getApiFieldValue(key);
          console.log(`EditResultPage - ${key}: 既存データを使用:`, apiValue);
          if (apiValue !== null) {
            apiData[apiKey] = apiValue;
          }
        } else if (value !== undefined && value !== null) {
          apiData[apiKey] = value;
        }
      }
    });

    if(!apiData['businesscardcreatetime'] && savedOcrData?.businesscardcreatetime) {
      apiData['businesscardcreatetime'] = savedOcrData.businesscardcreatetime;
    }

    //日付フォーマット変換
    if (apiData['businesscardcreatetime']) {
      const date = new Date(apiData['businesscardcreatetime']);
      apiData['businesscardcreatetime'] = date.toISOString();
    }

    //
    //
    
    // 画像URLがある場合はBase64エンコードに変換して設定
    if (scanResult?.data?.businesscardfrontimage) {
      apiData['businesscardfrontimage'] = scanResult.data.businesscardfrontimage;
    }

    // 裏面画像がある場合も設定
    console.log('EditResultPage - backSideImage:', backSideImage ? `存在(長さ: ${backSideImage.length})` : 'なし');
    if (backSideImage) {
      apiData['businesscardbackimage'] = backSideImage;
      console.log('EditResultPage - apiDataに裏面画像を設定しました');
    }

    // 本登録モード: 名刺画像が新規/既存で選択されている場合は、選択した側の画像を送る
    if (isPermanentMode) {
      const cd: any = scanResult?.apiResponse?.customerdata || {};
      const dd: any =
        (scanResult?.apiResponse as any)?.duplicatedata ||
        (scanResult?.apiResponse as any)?.duplicatelist?.[0] ||
        {};
      const useExisting = permanentCardImageSelection === 'existing';
      const frontImg = useExisting ? dd.businesscardfrontimage : cd.businesscardfrontimage;
      const backImg = useExisting ? dd.businesscardbackimage : cd.businesscardbackimage;
      if (frontImg) {
        apiData['businesscardfrontimage'] = frontImg;
      } else {
        delete apiData['businesscardfrontimage'];
      }
      if (backImg) {
        apiData['businesscardbackimage'] = backImg;
      } else {
        delete apiData['businesscardbackimage'];
      }
      console.log('EditResultPage - 本登録モードの名刺画像選択:', {
        selection: permanentCardImageSelection,
        hasFront: !!frontImg,
        hasBack: !!backImg,
      });
    }

    // 選択された担当者IDを配列として設定
    if (selectedContactUsers.length > 0) {
      apiData['contactuserno'] = JSON.stringify(selectedContactUsers);
    } else if (user?.id) {
      // 選択された担当者がない場合、ログイン中のユーザーIDを設定
      apiData['contactuserno'] = JSON.stringify([user.id]);
    } else {
      // ユーザーIDが取得できない場合はデフォルト値を設定
      apiData['contactuserno'] = '[1]';
    }
    
    // オプションフィールドを設定（既存の値があればそのまま、なければ空のオブジェクト）
    const storedScanResult = await indexedDBStorage.getItem('scanResult');
    let optionfieldObj: Record<string, any> = {};

    if (storedScanResult) {
      try {
        const result = JSON.parse(storedScanResult);
        let existingOptionfield = null;
        if (isPermanentMode) {
          // 本登録モードは既存顧客（重複）を編集対象とするため、既存の optionfield を基底にする。
          // （表示も既存顧客の拡張フィールドを seed しているため、保存基底も合わせる）
          existingOptionfield =
            result.apiResponse?.duplicatedata?.optionfield ??
            result.apiResponse?.duplicatelist?.[0]?.optionfield ??
            result.apiResponse?.customerdata?.optionfield ??
            null;
        } else if (result.apiResponse?.customerdata?.optionfield) {
          // 通常フロー 優先順位: customerdata > duplicatedata > duplicatelist[0]
          existingOptionfield = result.apiResponse.customerdata.optionfield;
        } else if (result.apiResponse?.duplicatedata?.optionfield) {
          existingOptionfield = result.apiResponse.duplicatedata.optionfield;
        } else if (result.apiResponse?.duplicatelist && result.apiResponse.duplicatelist.length > 0) {
          existingOptionfield = result.apiResponse.duplicatelist[0].optionfield;
        }

        if (existingOptionfield) {
          // 既存のoptionfieldをパース
          optionfieldObj = typeof existingOptionfield === 'string'
            ? JSON.parse(existingOptionfield)
            : { ...existingOptionfield }; // オブジェクトの場合はコピーを作成
          console.log('EditResultPage - 既存optionfield読み込み:', optionfieldObj);
        }
      } catch (error) {
        console.error('EditResultPage - optionfield解析エラー:', error);
        // エラーの場合は空オブジェクトで続行
        optionfieldObj = {};
      }
    }

    // 本登録モード: AdditionalInfoSection で編集した拡張フィールドの現在値を上書きで反映
    if (isPermanentMode) {
      optionfieldObj = { ...optionfieldObj, ...permanentOptionFieldValues };
      console.log('EditResultPage - 本登録モードの拡張フィールドを反映:', optionfieldObj);
    }

    // フリガナの値を保存（フリガナフィールドが存在する場合のみ）
    if (furiganaField) {
      // furiganaSelectionで既存が選択されている場合はfuriganaExistingValueを使用
      const furiganaToSave = furiganaSelection === 'existing'
        ? furiganaExistingValue
        : furiganaValue;
      optionfieldObj.furigana = furiganaToSave || ''; // 空文字の場合も明示的に保存
      console.log('EditResultPage - furigana値を保存:', {
        furiganaValue,
        furiganaSelection,
        furiganaNewValue,
        furiganaExistingValue,
        furiganaToSave,
        optionfieldObj,
        existingKeys: Object.keys(optionfieldObj)
      });
    }

    apiData['optionfield'] = JSON.stringify(optionfieldObj);
    console.log('EditResultPage - optionfield(JSON):', apiData['optionfield']);
    
    // メール配信フラグを設定（配信NGの逆）
    if (formData.doNotDistribute !== undefined) {
      apiData['maildeliveryflag'] = !formData.doNotDistribute;
    } else if(scanResult?.apiResponse?.duplicatelist && scanResult.apiResponse.duplicatelist.length > 0) {
      apiData['maildeliveryflag'] = scanResult.apiResponse.duplicatelist[0].maildeliveryflag;
    }
    else{apiData['maildeliveryflag'] = true;}
    
    // 既存のランクと備考情報を保持（既存顧客の場合のみ）
    // 優先順位: customerdata > duplicatedata > duplicatelist[0]
    let sourceData = null;
    if (scanResult?.apiResponse?.customerdata) {
      sourceData = scanResult.apiResponse.customerdata;
    } else if (scanResult?.apiResponse?.duplicatedata) {
      sourceData = scanResult.apiResponse.duplicatedata;
    } else if (scanResult?.apiResponse?.duplicatelist && scanResult.apiResponse.duplicatelist.length > 0) {
      sourceData = scanResult.apiResponse.duplicatelist[0];
    }

    if (isPermanentMode) {
      // 本登録モード: AdditionalInfoSection で選択した現在のランク・備考を使用する
      // （備考は新規/既存トグルで選んだ側の値がフォームに反映済み）
      const formRankno = form.getFieldValue('rankno');
      const formNotes = form.getFieldValue('notes');
      if (formRankno !== undefined && formRankno !== null) {
        apiData['rankno'] = formRankno;
      }
      apiData['remarks'] = formNotes ?? '';
      console.log('EditResultPage - 本登録モードのランク・備考を反映:', {
        rankno: apiData['rankno'],
        remarks: apiData['remarks'],
      });
    } else if (sourceData) {
      if (sourceData.remarks !== undefined && sourceData.remarks !== null) {
        apiData['remarks'] = sourceData.remarks;
      }
      if (sourceData.rankno !== undefined && sourceData.rankno !== null) {
        apiData['rankno'] = sourceData.rankno;
      }
    }
    
    apiData['delflag'] = false;

    console.log('EditResultPage - 変換完了後のAPIデータ:', apiData);
    console.log('EditResultPage - 裏面画像が含まれているか:', !!apiData['businesscardbackimage']);
    if (apiData['businesscardbackimage']) {
      console.log('EditResultPage - 裏面画像データの長さ:', apiData['businesscardbackimage'].length);
    }
    console.log('==========================================');

    return apiData;
  };
  
  // エラーメッセージから顧客番号を抽出する関数
  const extractCustomerNoFromError = (errorMessage: string): number | null => {
    // 「[顧客No.67505]」のような形式から数字を抽出
    const match = errorMessage.match(/\[顧客No\.(\d+)\]/);
    return match ? parseInt(match[1], 10) : null;
  };

  // API呼び出しを行う関数
  const submitToApi = async (formData: BusinessCard): Promise<boolean> => {
    try {
      console.log('===== EditResultPage - API登録開始 =====');
      setSubmittingToApi(true);
      setApiError(null);

      // ビジネスカードデータをAPIリクエスト形式に変換
      const apiRequestData = await convertToApiRequest(formData);
      console.log('EditResultPage - API呼び出し実行:', apiRequestData);

      let response;

      // 重複データが存在する場合は既存顧客更新として処理
      const hasDuplicateData = apiResponseData && apiResponseData.customerno;
      let targetCustomerNo = editingCustomerNo || (hasDuplicateData ? apiResponseData.customerno : null);

      // 本登録モード（仮登録顧客一覧からの「本登録して次へ」）の場合の登録先判定:
      // ・重複ユーザがいる → 新規作成せず、先頭の重複ユーザ(duplicatelist[0])を現在の選択内容で更新（editCustomer）
      // ・重複ユーザがいない → これまで通り新規登録（uploadBusinessCard）
      if (isPermanentMode) {
        const dupList = scanResult?.apiResponse?.duplicatelist;
        const firstDup =
          dupList && dupList.length > 0
            ? dupList[0]
            : scanResult?.apiResponse?.duplicatedata || null;
        targetCustomerNo = firstDup && firstDup.customerno ? firstDup.customerno : null;
        console.log('EditResultPage - 本登録モードの登録先 customerno:', targetCustomerNo);
        // 本登録完了後の仮登録レコード削除は handleSubmit 側（登録成功後）で実施
      }

      // 名刺取り込み後は追加項目のチェックを行わない
      apiRequestData.skipoptionfieldcheckflag = true;

      // 新規か既存かで使用するAPIを切り替え
      if (isPermanentMode) {
        // 仮登録の本登録：/api2/customercompare/regist を使用
        // customerno=仮登録顧客No、dupeCustomerno=重複する既存本登録顧客No（無ければnull）
        const tempCustomerNo = scanResult?.apiResponse?.customerdata?.customerno ?? null;
        const dupeCustomerno = targetCustomerNo; // 上の本登録モード分岐で重複顧客Noが入る（無ければnull）

        // 担当者No一覧（数値配列）
        const contactUserList =
          selectedContactUsers.length > 0
            ? selectedContactUsers
            : user?.id
            ? [user.id]
            : [];

        // 備考: チェックボックスの状態を反映（新規・既存を両方/片方反映）
        // remarksPriority: 1=新規のみ, 2=既存のみ, 3=両方。isRemarksNotUpdate: 既存を含む場合true
        const existingChecked = permanentRemarksExistingChecked;
        // 新規備考は編集可能なため、現在のフォーム値（notes）を送る
        const newRemarksToSend = (form.getFieldValue('notes') as string) ?? permanentNewRemarks ?? '';
        // 新規が空のときは結合対象から外す（空が "null" として新たに混入・蓄積するのを防ぐ）。
        // ※既存側(oldRemarks)に既に入っている "null" は削除せずそのまま保持・表示する。
        const newChecked = permanentRemarksNewChecked && newRemarksToSend !== '';
        const remarksPriority =
          newChecked && existingChecked ? 3 : existingChecked ? 2 : 1;

        // 各フィールドの「既存（未更新）」フラグ。既存選択時はサーバ側で更新しない
        const notUpdateFlag = (fieldKey: string) => fieldSelections[fieldKey] === 'existing';

        // regist が返す「次の仮登録顧客」も、一覧と同じく常にログインユーザ登録分のみとするため
        // isAllUser は常に false（仕様変更。元: 一覧の絞り込み設定に揃えていた）。
        // 今後の為、元のロジックはコメントで残す:
        // const tempListSelfOnly = (() => {
        //   try {
        //     const saved = localStorage.getItem('temp_customer_list_self_only');
        //     return saved === null ? true : saved === 'true';
        //   } catch { return true; }
        // })();

        const registPayload: Record<string, any> = {
          ...apiRequestData,
          customerno: tempCustomerNo, // 仮登録顧客No
          dupeCustomerno: dupeCustomerno, // 本登録（重複）顧客No。無ければnull
          contactUserList,
          // 備考
          remarksPriority, // 1=新規備考、2=既存備考、3=両方
          newRemarks: newRemarksToSend,
          oldRemarks: permanentExistingRemarks,
          isRemarksNotUpdate: existingChecked, // 既存側を含む場合は既存の備考を保持
          doMergeRemarks: permanentDoMergeRemarks, // 「顧客備考」欄の重複整備を行うか
          // 各フィールド未更新フラグ（既存選択時true）
          isRanknoNotUpdate: notUpdateFlag('rankno'),
          isCompanynameNotUpdate: notUpdateFlag('company'),
          isDeptnameNotUpdate: notUpdateFlag('department'),
          isPositionNotUpdate: notUpdateFlag('position'),
          isMobiletelNotUpdate: notUpdateFlag('mobile'),
          isFaxNotUpdate: notUpdateFlag('fax'),
          isPostcodeNotUpdate: notUpdateFlag('postalCode'),
          isAddressNotUpdate: notUpdateFlag('address'),
          isCountrynameNotUpdate: notUpdateFlag('country'),
          isNearstationNotUpdate: false,
          isUrlNotUpdate: notUpdateFlag('url'),
          // ページング（必須。明細は別途取得するため最小限）
          page: 1,
          isAllUser: false, // 常にログインユーザ登録分のみ（仕様）
          searchCreatefunction: 9, // 「次の仮登録顧客」も UP-TONE 由来のみに絞る（一覧 repage と条件を揃える）
          sortKey: 0,
          sortOrder: false,
        };
        // contactuserno は contactUserList を使うため、remarks は備考選択フィールドを使うため除去
        delete registPayload.contactuserno;
        delete registPayload.remarks;

        console.log('EditResultPage - 仮登録の本登録（regist）:', registPayload);
        response = await businessCardAPI.registTemporaryCustomer(registPayload);

        // 本登録成功時は、次の仮登録顧客への切替を handleSubmit 側で行うため、
        // regist のレスポンス（次の顧客 customerdata / duplicatedata を含む）を保持して早期return。
        // 以降の scanResult 更新（regist のレスポンスは「次の」顧客を返すため流用不可）はスキップ
        if (response.statuscode === 200) {
          console.log('EditResultPage - 仮登録の本登録 成功');
          permanentRegistResponseRef.current = response;
          return true;
        }
        // 失敗時は下の既存エラーハンドリングへ流す
      } else if (targetCustomerNo) {
        // 既存顧客の場合：editCustomer APIを使用
        console.log('EditResultPage - 既存顧客として更新:', targetCustomerNo);
        // customernoを明示的に設定
        apiRequestData.customerno = targetCustomerNo;
        response = await businessCardAPI.editCustomer(apiRequestData);
      } else {
        // 新規顧客の場合：uploadBusinessCard APIを使用
        // 解析に使ったAIの種別を付与（ボタン押下時点の UseChatGPT 現在値: ChatGPT=2 / Gemini=1）
        apiRequestData.businesscardocrtype = await getBusinessCardOcrType();
        console.log('EditResultPage - 新規顧客として登録 businesscardocrtype:', apiRequestData.businesscardocrtype);
        response = await businessCardAPI.uploadBusinessCard(apiRequestData);
      }

      console.log('EditResultPage - APIレスポンス:', response);

      // レスポンスの検証
      if (response.statuscode === 200) {
        console.log('EditResultPage - API登録成功');

        // 新規登録の場合、返却された顧客番号を保存
        if (!editingCustomerNo && response.customerdata && response.customerdata.customerno) {
          const newCustomerNo = response.customerdata.customerno;
          setEditingCustomerNo(newCustomerNo);
          setExcludeFromDuplicateCheckCustomerNo(newCustomerNo);
          await indexedDBStorage.setItem('editingCustomerNo', String(newCustomerNo));
          await indexedDBStorage.setItem('excludeFromDuplicateCheckCustomerNo', String(newCustomerNo));
          console.log('EditResultPage - 新規登録後の顧客番号を保存:', newCustomerNo);
        }

        // 成功した場合はScanResultを更新して保存
        if (scanResult) {
          formData.businesscardfrontimage = scanResult.data?.businesscardfrontimage;
          // 裏面画像も保存
          if (backSideImage) {
            formData.businesscardbackimage = backSideImage;
          }
          if(response.duplicatedata) {
            formData.contactuserno = response.duplicatedata.contactuserno;
          }else if(response.customerdata) {
            formData.contactuserno = response.customerdata.contactuserno;
          }
          //
          // 日付フォーマットはYYYY-MM-DD HH:MM:SS形式のまま保持
          // （API送信時にはconvertToApiRequestでISO形式に変換済み）
          //
          // fieldSelections で「既存」を選択したフィールドは既存顧客の値で上書きする
          // （フォームフィールドは意図的にOCR値のままなので、ここで解決する）
          const resolvedData = { ...formData };
          Object.keys(fieldSelections).forEach(key => {
            if (fieldSelections[key] === 'existing') {
              const existingVal = getApiFieldValue(key);
              if (existingVal !== null) {
                (resolvedData as any)[key] = existingVal;
              }
            }
          });
          scanResult.data = resolvedData;

          // APIレスポンスを更新する際、既存のランクと備考を保持
          const oldResponse = scanResult.apiResponse;
          let oldRankno = null;
          let oldRemarks = null;

          // customerdata, duplicatedata, duplicatelist[0] の順で確認
          if (oldResponse?.customerdata?.rankno !== null && oldResponse?.customerdata?.rankno !== undefined) {
            oldRankno = oldResponse.customerdata.rankno;
          } else if (oldResponse?.duplicatedata?.rankno !== null && oldResponse?.duplicatedata?.rankno !== undefined) {
            oldRankno = oldResponse.duplicatedata.rankno;
          } else if (oldResponse?.duplicatelist?.[0]?.rankno !== null && oldResponse?.duplicatelist?.[0]?.rankno !== undefined) {
            oldRankno = oldResponse.duplicatelist[0].rankno;
          }

          if (oldResponse?.customerdata?.remarks !== null && oldResponse?.customerdata?.remarks !== undefined) {
            oldRemarks = oldResponse.customerdata.remarks;
          } else if (oldResponse?.duplicatedata?.remarks !== null && oldResponse?.duplicatedata?.remarks !== undefined) {
            oldRemarks = oldResponse.duplicatedata.remarks;
          } else if (oldResponse?.duplicatelist?.[0]?.remarks !== null && oldResponse?.duplicatelist?.[0]?.remarks !== undefined) {
            oldRemarks = oldResponse.duplicatelist[0].remarks;
          }

          // optionfieldも保持
          let oldOptionfield = null;
          if (oldResponse?.customerdata?.optionfield) {
            oldOptionfield = oldResponse.customerdata.optionfield;
          } else if (oldResponse?.duplicatedata?.optionfield) {
            oldOptionfield = oldResponse.duplicatedata.optionfield;
          } else if (oldResponse?.duplicatelist?.[0]?.optionfield) {
            oldOptionfield = oldResponse.duplicatelist[0].optionfield;
          }

          console.log('EditResultPage - 保存されていたランク、備考、optionfield:', { oldRankno, oldRemarks, oldOptionfield });

          scanResult.apiResponse = response;

          // 新しいレスポンスにランク、備考、optionfieldを復元/マージ
          // APIレスポンスには含まれていない可能性が高いため、常に復元する
          if (response.customerdata) {
            if (oldRankno !== null && oldRankno !== undefined) {
              response.customerdata.rankno = oldRankno;
              console.log('EditResultPage - customerdata.ranknoを復元:', oldRankno);
            }
            if (oldRemarks !== null && oldRemarks !== undefined) {
              response.customerdata.remarks = oldRemarks;
              console.log('EditResultPage - customerdata.remarksを復元:', oldRemarks);
            }
            // optionfieldはマージする（APIレスポンスを優先）
            if (oldOptionfield !== null && oldOptionfield !== undefined) {
              try {
                const oldOptionfieldObj = typeof oldOptionfield === 'string'
                  ? JSON.parse(oldOptionfield)
                  : { ...oldOptionfield };
                const newOptionfieldObj = response.customerdata.optionfield
                  ? (typeof response.customerdata.optionfield === 'string'
                      ? JSON.parse(response.customerdata.optionfield)
                      : { ...response.customerdata.optionfield })
                  : {};
                // 古いoptionfieldをベースに、新しいoptionfieldで上書き（マージ）
                const mergedOptionfield = { ...oldOptionfieldObj, ...newOptionfieldObj };
                response.customerdata.optionfield = JSON.stringify(mergedOptionfield);
                console.log('EditResultPage - customerdata.optionfieldをマージ:', {
                  old: oldOptionfieldObj,
                  new: newOptionfieldObj,
                  merged: mergedOptionfield
                });
              } catch (error) {
                console.error('EditResultPage - optionfield マージエラー:', error);
                // エラー時は古いoptionfieldをそのまま使用
                response.customerdata.optionfield = oldOptionfield;
              }
            }
          } else if (response.duplicatedata) {
            if (oldRankno !== null && oldRankno !== undefined) {
              response.duplicatedata.rankno = oldRankno;
              console.log('EditResultPage - duplicatedata.ranknoを復元:', oldRankno);
            }
            if (oldRemarks !== null && oldRemarks !== undefined) {
              response.duplicatedata.remarks = oldRemarks;
              console.log('EditResultPage - duplicatedata.remarksを復元:', oldRemarks);
            }
            // optionfieldはマージする（APIレスポンスを優先）
            if (oldOptionfield !== null && oldOptionfield !== undefined) {
              try {
                const oldOptionfieldObj = typeof oldOptionfield === 'string'
                  ? JSON.parse(oldOptionfield)
                  : { ...oldOptionfield };
                const newOptionfieldObj = response.duplicatedata.optionfield
                  ? (typeof response.duplicatedata.optionfield === 'string'
                      ? JSON.parse(response.duplicatedata.optionfield)
                      : { ...response.duplicatedata.optionfield })
                  : {};
                // 古いoptionfieldをベースに、新しいoptionfieldで上書き（マージ）
                const mergedOptionfield = { ...oldOptionfieldObj, ...newOptionfieldObj };
                response.duplicatedata.optionfield = JSON.stringify(mergedOptionfield);
                console.log('EditResultPage - duplicatedata.optionfieldをマージ:', {
                  old: oldOptionfieldObj,
                  new: newOptionfieldObj,
                  merged: mergedOptionfield
                });
              } catch (error) {
                console.error('EditResultPage - optionfield マージエラー:', error);
                // エラー時は古いoptionfieldをそのまま使用
                response.duplicatedata.optionfield = oldOptionfield;
              }
            }
          }

          console.log('EditResultPage - scanResultを更新:', scanResult);
          // IndexedDBに更新したScanResultを保存
          await indexedDBStorage.setItem('scanResult', JSON.stringify(scanResult));
          await indexedDBStorage.setItem('editResultBackup', JSON.stringify(scanResult));
          console.log('EditResultPage - scanResultをIndexedDBに保存完了');

          // メモリ上のscanResult stateも更新
          setScanResult({...scanResult});
          console.log('EditResultPage - scanResult stateを更新');

          // 登録済みデータを更新（次回の初期値として使用）
          const registeredApiData = response.duplicatedata || response.customerdata;
          if (registeredApiData) {
            const registeredData: Partial<BusinessCard> = {};
            Object.entries(registeredApiData).forEach(([apiKey, value]) => {
              const fieldKey = apiFieldMapping[apiKey];
              if (fieldKey && value !== undefined && value !== null) {
                // 日付フィールドの場合はISO形式からYYYY-MM-DD HH:MM:SS形式に変換
                if (fieldKey === 'businesscardcreatetime' && typeof value === 'string') {
                  try {
                    const date = new Date(value);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    const seconds = String(date.getSeconds()).padStart(2, '0');
                    (registeredData as any)[fieldKey] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                  } catch (e) {
                    (registeredData as any)[fieldKey] = String(value);
                  }
                } else {
                  (registeredData as any)[fieldKey] = String(value);
                }
              }
            });

            // maildeliveryflag を doNotDistribute に変換（逆のロジック）
            if (registeredApiData.maildeliveryflag !== undefined) {
              (registeredData as any).doNotDistribute = !registeredApiData.maildeliveryflag;
            }

            setSavedRegisteredData(registeredData);
            console.log('EditResultPage - savedRegisteredDataを更新:', registeredData);
          }
        }

        console.log('==========================================');
        return true;
      } else {
        console.log('EditResultPage - API登録エラー:', response.statuscode, response.message);
        const errorMessage = response.message || 'APIへの登録に失敗しました';

        // 重複リストが2件以上ある場合は専用メッセージ
        const duplicateCount = scanResult?.apiResponse?.duplicatelist?.length || 0;
        if ((editingCustomerNo && duplicateCount === 1) || duplicateCount >= 2) {
          setApiError(
            "重複した「顧客情報」が2件以上データベースに存在します。\n" +
            "まずはPC上で（ACS-Lightにログインし）「顧客データ重複整備」を行ってから再度お試しください。"
          );
          window.scrollTo(0, 0);
          console.log('EditResultPage - 重複データ検出:', duplicateCount, '件');
          console.log('==========================================');
          return false;
        }

        // エラーメッセージから顧客番号を抽出
        const duplicateCustomerNo = extractCustomerNoFromError(errorMessage);
        console.log('EditResultPage - エラーから抽出した顧客番号:', duplicateCustomerNo);
        
        if (duplicateCustomerNo) {
          setApiError("新たに重複している顧客が見つかりました。\r\n顧客情報を再確認してください。");
          // エラー時に画面の一番上にスクロール
          window.scrollTo(0, 0);
          console.log('==========================================');
          checkDuplicateCustomer();
          return false;
        } else {
          console.log('EditResultPage - 重複顧客番号なし、エラー設定');
          setApiError(errorMessage);
          // エラー時に画面の一番上にスクロール
          window.scrollTo(0, 0);
          console.log('==========================================');
          return false;
        }
      }
    } catch (error: any) {
      console.error('EditResultPage - API呼び出し例外:', error);
      setApiError(error.message || 'サーバーとの通信に失敗しました');
      // エラー時に画面の一番上にスクロール
      window.scrollTo(0, 0);
      console.log('==========================================');
      return false;
    } finally {
      setSubmittingToApi(false);
    }
  };
  
  // 「登録」ボタンの処理
  const handleSubmit = async () => {
    try {
      // フォームの全値を取得
      const allValues = form.getFieldsValue();

      // 全フィールドの検証
      let isValid = true;
      let errorMessages: string[] = [];
      const validityState: {[key: string]: {valid: boolean, message?: string}} = {};

      // 顧客名のチェック
      if (!allValues.name || allValues.name.trim() === '') {
        isValid = false;
        errorMessages.push('顧客名を入力してください');
        validityState['name'] = { valid: false, message: '顧客名を入力してください' };
      } else {
        validityState['name'] = { valid: true };
      }

      // 電話番号ORメールアドレスのチェック
      // fieldSelectionsの状態を考慮して、実際に使用される値を取得
      const phoneValue = fieldSelections['phone'] === 'existing'
        ? getApiFieldValue('phone')
        : allValues.phone;
      const emailValue = fieldSelections['email'] === 'existing'
        ? getApiFieldValue('email')
        : allValues.email;

      const hasPhone = phoneValue && String(phoneValue).trim() !== '';
      const hasEmail = emailValue && String(emailValue).trim() !== '';

      if (!hasPhone && !hasEmail) {
        isValid = false;
        errorMessages.push('電話番号またはメールアドレスのいずれかを入力してください');
        validityState['phone'] = { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' };
        validityState['email'] = { valid: false, message: '電話番号またはメールアドレスのいずれかを入力してください' };
      } else {
        validityState['phone'] = { valid: true };
        validityState['email'] = { valid: true };
      }

      // メールアドレスの形式チェック
      // 既存が選択されている場合は、既存データの値をチェック
      if (hasEmail && !validateField('email', emailValue)) {
        isValid = false;
        errorMessages.push('メールアドレスの形式が正しくありません');
        validityState['email'] = { valid: false, message: 'メールアドレスの形式が正しくありません' };
      }

      // postalCodeのバリデーションは海外住所対応のため削除

      // バリデーション状態を更新
      setFormValidity(validityState);

      if (!isValid) {
        // エラーメッセージをすべて表示
        errorMessages.forEach(msg => message.error(msg));

        // 最初のエラーフィールドまでスクロール
        const firstErrorField = Object.keys(validityState).find(key => validityState[key].valid === false);
        if (firstErrorField) {
          // 少し遅延させてDOMの更新を待つ
          setTimeout(() => {
            const errorElement = document.querySelector(`[data-field-key="${firstErrorField}"]`);
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }

        return;
      }

      setLoading(true);

      // APIに登録
      const apiSuccess = await submitToApi(allValues);

      if (apiSuccess) {
        message.success('顧客情報を登録しました');
        // 一時画像を削除
        await deleteTemporaryImage();

        if (isPermanentMode) {
          // regist API が仮登録レコードの本登録・消費を行うため、ここでの仮登録レコード削除は不要
          // 本登録モード: regist のレスポンスが返す次の仮登録顧客へ自動切替（無ければ一覧に戻る）
          await handleAdvanceToNextTempCustomer();
          // 同ページに留まる（次の顧客へ）場合があるため loading を解除
          // 一覧に戻った場合はアンマウントされるので解除は無害
          setLoading(false);
        } else {
          // 緊急モードは追加情報画面をスキップしてフォロー登録へ（skipadditionalinfoflag と同じ扱い）
          const goFollow = (user?.skipadditionalinfoflag || false) || emergencyMode;
          // 追加情報入力画面もしくはフォローの登録画面に遷移
          navigate(getRoutePath(goFollow ? ROUTES.FOLLOW_REGISTRATION : ROUTES.ADDITIONAL_INFO));
        }
      } else {
        setLoading(false);
        // エラーメッセージはsubmitToApi内で設定される
      }
    } catch (error: any) {
      message.error('登録に失敗しました');
      setLoading(false);
    }
  };

  // 本登録モードの名刺画像（新規=仮登録側 / 既存=重複顧客側）
  const permCardNewFront: string | null =
    (scanResult?.apiResponse?.customerdata as any)?.businesscardfrontimage ||
    scanResult?.data?.businesscardfrontimage ||
    null;
  const permCardExistingFront: string | null =
    (scanResult?.apiResponse as any)?.duplicatedata?.businesscardfrontimage ||
    (scanResult?.apiResponse as any)?.duplicatelist?.[0]?.businesscardfrontimage ||
    null;
  // 本登録モードで重複顧客があり、いずれかに名刺画像があれば新規/既存の選択UIを表示
  const showPermCardImageToggle =
    isPermanentMode && permanentHasExisting && (!!permCardNewFront || !!permCardExistingFront);
  // 選択中の表面画像（プレビュー・送信表示に使用）
  const permSelectedFront: string | null =
    permanentCardImageSelection === 'existing' ? permCardExistingFront : permCardNewFront;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <Button
          shape="circle"
          size="small"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{
            backgroundColor: 'white',
            borderColor: '#d9d9d9',
            borderWidth: '2px',
            color: '#595959',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        />
        <div style={{ flex: 1, textAlign: 'center' }}>
          {isManualInput ? '顧客情報の確認・編集' : '名刺情報の確認・編集'}
          {/*{editingCustomerNo && <span style={{ fontSize: '12px', marginLeft: '5px', color: '#666' }}>(顧客No: {editingCustomerNo})</span>}*/}
        </div>
      </Header>
      
      <Content style={{
        padding: '16px',
        paddingBottom: `${16 + flutterFooterHeight + 80}px`, // 16px基本余白 + Flutterフッター高さ + 固定ボタン高さ(約80px)
        background: '#f0f2f5',
        overflowY: 'auto'
      }}>
        {/* API エラーメッセージ */}
        {apiError && (
          <Card style={{ marginBottom: 16, backgroundColor: '#ffebee' }}>
            <div style={{ color: '#d32f2f' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>エラー</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{apiError}</div>
            </div>
          </Card>
        )}
        
        {/* 本登録モード: 名刺画像の新規/既存選択 */}
        {showPermCardImageToggle && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold', color: '#595959' }}>名刺画像</div>
            {(['new', 'existing'] as const).map((side) => {
              const img = side === 'new' ? permCardNewFront : permCardExistingFront;
              const selected = permanentCardImageSelection === side;
              return (
                <div
                  key={side}
                  onClick={() => setPermanentCardImageSelection(side)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: side === 'new' ? 8 : 0,
                    padding: '8px',
                    backgroundColor: selected ? '#0693e3' : '#f9f9f9',
                    border: `1px solid ${selected ? '#0693e3' : '#d9d9d9'}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  <Radio
                    checked={selected}
                    onChange={(e) => e.stopPropagation()}
                    style={{ marginRight: 8 }}
                  />
                  <div
                    style={{
                      width: 48,
                      marginRight: 8,
                      textAlign: 'center',
                      color: selected ? 'white' : 'rgba(0, 0, 0, 0.45)',
                      fontWeight: selected ? 'bold' : 'normal',
                    }}
                  >
                    {side === 'new' ? '新規' : '既存'}
                  </div>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    {img ? (
                      <div style={{ display: 'inline-block', position: 'relative' }}>
                        <img
                          src={img}
                          alt={side === 'new' ? '仮登録の名刺' : '既存顧客の名刺'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '160px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            display: 'block',
                            backgroundColor: '#fff',
                          }}
                        />
                        <button
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0, 0, 0, 0.45)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#fff',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPermanentCardImageSelection(side);
                            setFrontPreviewOpen(true);
                          }}
                        >
                          <ZoomInOutlined />
                        </button>
                      </div>
                    ) : (
                      <Text style={{ color: selected ? 'white' : 'rgba(0, 0, 0, 0.45)' }}>
                        画像なし
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        )}

        {/* 読み取った画像 */}
        {!showPermCardImageToggle && scanResult?.data?.businesscardfrontimage && (
          <Card style={{ marginBottom: 16 }}>
            {/* 本登録モード（仮登録の本登録）では裏面取り込みは非表示 */}
            {!isPermanentMode && (
              <div style={{ marginBottom: 8, textAlign: 'right' }}>
                <Button
                  type="default"
                  icon={<CameraOutlined />}
                  size="small"
                  onClick={handleBackSideCapture}
                  style={{
                    backgroundColor: '#f5f5f5',
                    borderColor: '#d9d9d9',
                    color: '#595959'
                  }}
                >
                  裏面を取り込む
                </Button>
              </div>
            )}
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <img
                  src={scanResult?.data?.businesscardfrontimage}
                  alt="撮影した名刺"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
                <button
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setFrontPreviewOpen(true)}
                >
                  <ZoomInOutlined />
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* 裏面画像 */}
        {backSideImage && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 'bold', color: '#595959' }}>
              裏面画像
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <img
                  src={backSideImage}
                  alt="名刺の裏面"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
                <button
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setBackPreviewOpen(true)}
                >
                  <ZoomInOutlined />
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* 情報編集エリア */}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EditOutlined style={{ marginRight: 8 }} />
                情報編集
              </div>
              {/* 緊急モード（UseChatGPT=true）で解析した結果である旨の注記。
                  ボタンと誤認されないよう、枠線・背景・hover の無いプレーンな注記テキストにする。 */}
              {!isPermanentMode && emergencyMode && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#cf1322',
                  fontSize: 12,
                  cursor: 'default',
                  userSelect: 'none',
                }}>
                  <ExclamationCircleOutlined />
                  緊急モードで解析した結果です
                </span>
              )}
              {/* 標準モードで解析されたときのみ「高品質モードで再解析」を提示
                  （緊急モード時・仮登録の本登録モードでは非表示） */}
              {!isPermanentMode && !emergencyMode && lastOcrModel === 'fast' && (
                <Button
                  type="primary"
                  icon={<RetweetOutlined />}
                  onClick={handleRescan}
                  size="small"
                  loading={rescanning}
                >
                  高品質モードで再解析
                </Button>
              )}
            </div>
          }
          style={{ marginBottom: 16 }}
        >
          {/* 緊急モード初回登録時のフィールドロック用スタイル
              （全ロック＝既存検知 / 電話のみ＝全く新規。電話だけ操作可にする） */}
          <style>{`
            .emg-lock-all [data-field-key] { pointer-events: none; opacity: 0.5; }
            .emg-lock-phone [data-field-key] { pointer-events: none; opacity: 0.45; }
            /* 電話番号のみ操作可。編集可と分かるよう強調表示する */
            .emg-lock-phone [data-field-key="phone"] {
              pointer-events: auto;
              opacity: 1;
              background: #fffbe6;
              border: 1px solid #ffd666;
              border-radius: 6px;
              padding: 10px 12px;
            }
          `}</style>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onValuesChange={handleFormValuesChange}
            className={emergencyLockAll ? 'emg-lock-all' : (emergencyPhoneOnly ? 'emg-lock-phone' : undefined)}
          >
            {/* 緊急モード×全く新規：電話番号のみ編集可の案内 */}
            {emergencyPhoneOnly && (
              <div style={{
                marginBottom: 16,
                padding: '10px 12px',
                backgroundColor: '#fff1f0',
                border: '1px solid #ffa39e',
                borderRadius: 6,
              }}>
                <Text style={{ color: '#cf1322', fontSize: 13 }}>
                  <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                  緊急モードの為、電話番号のみ編集できます
                </Text>
              </div>
            )}
            {dataFields.map((field, index) => {
              const isNameField = field.key === 'name';

              // 担当者フィールドの場合は別処理
              if (field.key === 'representative') {
                return (
                  <div key={field.key} data-field-key={field.key} style={{ marginBottom: 24 }}>
                    {/* フィールドのラベル */}
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{field.label}{field.required ? <span style={{ color: 'red' }}> *</span> : ''}</Text>
                    </div>

                    {/* 担当者表示部分（選択UIのみ表示） */}
                    <Form.Item name="representative" style={{ margin: 0, display: 'none' }}>
                      <Input type="hidden" />
                    </Form.Item>
                    <div>
                      {renderContactUsers()}
                    </div>
                  </div>
                );
              }

              // 配信NGフィールドの場合は特別処理
              if (field.key === 'doNotDistribute') {
                const currentValue = form.getFieldValue('doNotDistribute');
                const mailDeliveryFlag = apiResponseData?.maildeliveryflag;
                const displayValue = currentValue !== undefined 
                  ? (currentValue ? 'NG' : 'OK')
                  : (mailDeliveryFlag === false ? 'NG' : 'OK');

                return (
                  <div key={field.key} data-field-key={field.key} style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{field.label}</Text>
                    </div>

                    <Form.Item name="doNotDistribute" style={{ marginBottom: 0 }}>
                      <Select 
                        placeholder="配信設定を選択"
                        value={currentValue !== undefined ? currentValue : (mailDeliveryFlag === false)}
                        onChange={(value) => {
                          form.setFieldsValue({ doNotDistribute: value });
                        }}
                      >
                        <Select.Option value={false}>OK</Select.Option>
                        <Select.Option value={true}>NG</Select.Option>
                      </Select>
                    </Form.Item>
                  </div>
                );
              }

              const hasApiDataFieldResult = hasApiDataField(field.key);
              const selectionType = fieldSelections[field.key] || 'new';

              // フォームの現在値とAPIデータが同一かチェック
              const currentFormValue = form.getFieldValue(field.key as keyof BusinessCard);
              const apiData = getApiFieldValue(field.key);

              // 空文字列、null、undefinedを「空」として扱う
              const isCurrentEmpty = !currentFormValue || currentFormValue === '';
              const isApiDataEmpty = !apiData || apiData === '';

              const isSameValue =
                // 両方が空の場合は同じ値とみなす
                (isCurrentEmpty && isApiDataEmpty) ||
                // 両方が非空で、実際の値が同じ場合
                (!isCurrentEmpty && !isApiDataEmpty &&
                  ([
                    'postalCode',
                    'phone',
                    'mobile',
                    'fax'
                  ].includes(field.key)
                    // Compare numeric values without non-digits for postal, phone, mobile, fax
                    ? String(currentFormValue).replace(/\D/g, '') === apiData.replace(/\D/g, '')
                    // Direct comparison for other fields
                    : String(currentFormValue).trim() === (apiData).trim()
                  )
                );

              // 重複データまたは登録済みデータが存在する場合、ユーザーが入力したら選択UIを表示
              // （フィールドがデータに存在しない場合でも）
              const hasUserInput = currentFormValue !== null &&
                currentFormValue !== undefined &&
                currentFormValue !== '';
              const hasApiDataForField = hasApiDataFieldResult ||
                ((apiResponseData || savedRegisteredData) && hasUserInput);
              
              // OCRキーを見つける
              //
              const ocrKey = Object.entries(apiRequestMapping).find(([_, val]) => val === field.key)?.[0];
              
              //
              //
              
              // OCRデータから値を取得
              const ocrValue = ocrKey && savedOcrData ? 
                ((savedOcrData as Record<string, any>)[ocrKey] !== undefined && (savedOcrData as Record<string, any>)[ocrKey] !== null ? 
                  String((savedOcrData as Record<string, any>)[ocrKey]) : '') : '';

              //
              
              // API応答データから値を取得
              const apiValue = getApiFieldValue(field.key) || '';
              
              // 担当者フィールドの場合、特別な処理をする
              if (field.key === 'representative') {
                return (
                  <div key={field.key} data-field-key={field.key} style={{ marginBottom: 24 }}>
                    {/* フィールドのラベル */}
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>{field.label}{field.required ? <span style={{ color: 'red' }}> *</span> : ''}</Text>
                    </div>

                    {/* 担当者表示部分（選択UIのみ表示） */}
                    <Form.Item name="representative" style={{ margin: 0, display: 'none' }}>
                      <Input type="hidden" />
                    </Form.Item>
                    <div>
                      {renderContactUsers()}
                    </div>
                  </div>
                );
              }
              
              const fieldElement = (
                <div key={field.key} data-field-key={field.key} style={{ marginBottom: 24 }}>
                  {/* フィールドのラベル */}
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>{field.label}{field.required ? <span style={{ color: 'red' }}> *</span> : ''}</Text>
                  </div>

                  {/* 既存データがある場合の2行構成表示 */}
                  {hasApiDataForField && !isSameValue ? (
                    <div>
                      
                          {/* 1行目: 新規データ (OCRデータ) */}
                          <div 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: 8,
                              padding: '8px',
                              backgroundColor: isSelected(selectionType, 'new') ? '#0693e3' : '#f9f9f9',
                              border: `1px solid ${isSelected(selectionType, 'new') ? '#0693e3' : '#d9d9d9'}`,
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleFieldSelectionChange(field.key, 'new')}
                          >
                        <Radio 
                          checked={isSelected(selectionType, 'new')} 
                          onChange={(e) => e.stopPropagation()}
                          style={{ marginRight: 8 }}
                        />
                        <div 
                          style={{ 
                            width: 60, 
                            marginRight: 8,
                            backgroundColor: isSelected(selectionType, 'new') ? '#0693e3' : 'transparent',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            textAlign: 'center'
                          }}
                        >
                          <Text style={{ 
                            color: isSelected(selectionType, 'new') ? 'white' : 'rgba(0, 0, 0, 0.45)',
                            fontWeight: isSelected(selectionType, 'new') ? 'bold' : 'normal'
                          }}>新規</Text>
                        </div>
                        {isSelected(selectionType, 'new') ? (
                          <Form.Item
                            name={field.key as keyof BusinessCard}
                            style={{ margin: 0, flex: 1 }}
                            validateStatus={
                              (formValidity[field.key]?.valid === false)
                                ? 'error'
                                : undefined
                            }
                            help={formValidity[field.key]?.message}
                          >
                            {field.key === 'postalCode' ? (
                              <TextArea 
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                onChange={handlePostalCodeChange}
                                onBlur={handlePostalCodeBlur}
                              />
                            ) : field.key === 'email' ? (
                              <TextArea 
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                              />
                            
                            ) : field.key === 'address' ? (
                              <TextArea 
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1 }}
                              />
                            ) : field.key === 'name' ? (
                              <TextArea
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                onBlur={checkDuplicateCustomer}
                                onCompositionStart={() => {
                                  // 現在のフリガナ値を保存して追記モードにする
                                  autoKanaHandlerRef.current.handleCompositionStart(furiganaValue);
                                }}
                                onCompositionUpdate={(e) => {
                                  // ひらがなのみの場合のみカタカナに変換してフリガナに設定
                                  if (isHiraganaOnly(e.data || '')) {
                                    const kana = autoKanaHandlerRef.current.handleCompositionUpdate(e);
                                    if (furiganaField) {
                                      setFuriganaNewValue(kana);
                                      setFuriganaValue(kana);
                                    }
                                  }
                                }}
                                onCompositionEnd={() => {
                                  // 変換確定時はフリガナを更新しない（変換中のひらがなカタカナをキープ）
                                  autoKanaHandlerRef.current.reset(true); // previousValueは保持
                                }}
                              />
                            ) : field.key === 'phone' ? (
                              <TextArea
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                onChange={handlePhoneChange}
                                onBlur={handlePhoneBlur}
                              />
                            ) : (
                              <TextArea 
                                placeholder={`${field.label}を入力`}
                                autoSize={{ minRows: 1, maxRows: 3 }}
                              />
                            )}
                          </Form.Item>
                        ) : (
                          <div style={{ flex: 1, padding: '4px 8px' }}>
                            <Text style={{
                              color: (selectionType as string) === 'new' ? 'white' : 'rgba(0, 0, 0, 0.65)'
                            }}>
                              {(() => {
                                // フォームの現在値を直接使用（ユーザーの入力値を確実に反映）
                                const currentValue = form.getFieldValue(field.key as keyof BusinessCard);

                                if (currentValue === null || currentValue === undefined || currentValue === '') {
                                  return '-';
                                }

                                const rawValue = String(currentValue);

                                if (field.key === 'postalCode') {
                                  return formatPostalCodeDisplay(rawValue);
                                }
                                return rawValue;
                              })()}
                            </Text>
                          </div>
                        )}
                      </div>
                      
                      {/* 2行目: 既存データ (APIデータ) */}
                      <div 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: isSelected(selectionType, 'existing') ? '#0693e3' : '#f9f9f9',
                          border: `1px solid ${isSelected(selectionType, 'existing') ? '#0693e3' : '#d9d9d9'}`,
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleFieldSelectionChange(field.key, 'existing')}
                      >
                        <Radio 
                          checked={isSelected(selectionType, 'existing')} 
                          onChange={(e) => e.stopPropagation()}
                          style={{ marginRight: 8 }}
                        />
                        <div 
                          style={{ 
                            width: 60, 
                            marginRight: 8,
                            backgroundColor: isSelected(selectionType, 'existing') ? '#0693e3' : 'transparent',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            textAlign: 'center'
                          }}
                        >
                          <Text style={{ 
                            color: isSelected(selectionType, 'existing') ? 'white' : 'rgba(0, 0, 0, 0.45)',
                            fontWeight: isSelected(selectionType, 'existing') ? 'bold' : 'normal'
                          }}>既存</Text>
                        </div>
                        <div style={{ flex: 1, padding: '4px 8px' }}>
                          <Text style={{
                            color: (selectionType as string) === 'existing' ? 'white' : 'rgba(0, 0, 0, 0.65)'
                          }}>
                            {field.key === 'postalCode'
                              ? (formatPostalCodeDisplay(apiValue) || '-')
                              : ['phone', 'mobile', 'fax'].includes(field.key)
                                ? (formatPhoneNumberDisplay(apiValue) || '-')
                                : field.key === 'businesscardcreatetime'
                                  // 日付を「YYYY-MM-DD HH:mm:ss」で表示
                                  ? (apiValue
                                      ? (() => {
                                          const d = new Date(apiValue);
                                          const Y = d.getFullYear();
                                          const M = String(d.getMonth() + 1).padStart(2, '0');
                                          const D = String(d.getDate()).padStart(2, '0');
                                          const h = String(d.getHours()).padStart(2, '0');
                                          const m = String(d.getMinutes()).padStart(2, '0');
                                          const s = String(d.getSeconds()).padStart(2, '0');
                                          return `${Y}-${M}-${D} ${h}:${m}:${s}`;
                                        })()
                                      : '-')
                                  : (apiValue || '-')}
                          </Text>
                        </div>
                      </div>
                      
                      
                      {/* 既存データが選択されている場合の隠しフォームフィールド */}
                      {isSelected(selectionType, 'existing') && (
                        <Form.Item 
                          name={field.key as keyof BusinessCard} 
                          style={{ display: 'none' }}
                        >
                          <Input />
                        </Form.Item>
                      )}
                    </div>
                  ) : (
                    // 既存データがない場合は通常の入力フィールド（担当者フィールドは除く）
                    <Form.Item
                      name={field.key as keyof BusinessCard}
                      validateStatus={
                        (formValidity[field.key]?.valid === false)
                          ? 'error'
                          : undefined
                      }
                      help={formValidity[field.key]?.message}
                      rules={field.required && !(field as any).conditionalRequired ? [{ required: true, message: `${field.label}を入力してください` }] : undefined}
                      style={{ marginBottom: 0 }}
                    >
                      {field.key === 'postalCode' ? (
                        <TextArea 
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          onChange={handlePostalCodeChange}
                          onBlur={handlePostalCodeBlur}
                        />
                      ) : field.key === 'email' ? (
                        <TextArea 
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          onChange={handleEmailChange}
                          onBlur={handleEmailBlur}
                        />
                      
                      ) : field.key === 'address' ? (
                        <TextArea
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1 }}
                        />
                      ) : field.key === 'name' ? (
                        <TextArea
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          onBlur={checkDuplicateCustomer}
                          onCompositionStart={() => {
                            // 現在のフリガナ値を保存して追記モードにする
                            autoKanaHandlerRef.current.handleCompositionStart(furiganaValue);
                          }}
                          onCompositionUpdate={(e) => {
                            // ひらがなのみの場合のみカタカナに変換してフリガナに設定
                            if (isHiraganaOnly(e.data || '')) {
                              const kana = autoKanaHandlerRef.current.handleCompositionUpdate(e);
                              if (furiganaField) {
                                setFuriganaNewValue(kana);
                                setFuriganaValue(kana);
                              }
                            }
                          }}
                          onCompositionEnd={() => {
                            // 変換確定時はフリガナを更新しない（変換中のひらがなカタカナをキープ）
                            autoKanaHandlerRef.current.reset(true); // previousValueは保持
                          }}
                        />
                      ) : field.key === 'phone' ? (
                        <TextArea
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          onChange={handlePhoneChange}
                          onBlur={handlePhoneBlur}
                        />
                      ) : (
                        <TextArea 
                          placeholder={`${field.label}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                        />
                      )}
                    </Form.Item>
                  )}
                </div>
              );

              // 顧客名フィールドの後にフリガナフィールドを挿入
              if (isNameField && furiganaField) {
                // フィールド名から「（顧客）」などの括弧内の文字を除去
                const furiganaLabel = furiganaField.dispname.replace(/[（(][^）)]*[）)]/g, '').trim();

                // 既存フリガナデータがあるかチェック
                // 重複データまたは登録済みデータが存在し、ユーザーが入力した場合も選択UIを表示
                const hasFuriganaUserInput = furiganaValue !== null &&
                  furiganaValue !== undefined &&
                  furiganaValue !== '';
                const hasFuriganaApiData = (furiganaExistingValue !== '') ||
                  ((apiResponseData || savedRegisteredData) && hasFuriganaUserInput);

                // 新規と既存のフリガナが同じかチェック
                // 両方が空の場合も同じ値とみなす
                const isFuriganaNewEmpty = !furiganaNewValue || furiganaNewValue === '';
                const isFuriganaExistingEmpty = !furiganaExistingValue || furiganaExistingValue === '';
                const isSameFuriganaValue =
                  (isFuriganaNewEmpty && isFuriganaExistingEmpty) ||
                  (!isFuriganaNewEmpty && !isFuriganaExistingEmpty &&
                   furiganaNewValue === furiganaExistingValue);

                return (
                  <React.Fragment key={field.key}>
                    {fieldElement}
                    {/* data-field-key を付与し、緊急モードのロック CSS の対象に含める
                        （emg-lock-all=全ロック / emg-lock-phone=電話以外ロック の両方でフリガナをロック） */}
                    <div data-field-key="furigana" style={{ marginBottom: 24 }}>
                      {/* フィールドのラベル */}
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>{furiganaLabel}</Text>
                      </div>

                      {/* 既存データがある場合の2行構成表示 */}
                      {hasFuriganaApiData && !isSameFuriganaValue ? (
                        <div>
                          {/* 1行目: 新規データ (OCRデータ - 現時点では空) */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: 8,
                              padding: '8px',
                              backgroundColor: isSelected(furiganaSelection, 'new') ? '#0693e3' : '#f9f9f9',
                              border: `1px solid ${isSelected(furiganaSelection, 'new') ? '#0693e3' : '#d9d9d9'}`,
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              // すでに選択されている場合は処理をスキップ
                              if (furiganaSelection === 'new') {
                                console.log('EditResultPage - フリガナ：すでに新規が選択されているため処理をスキップ');
                                return;
                              }
                              setFuriganaSelection('new');
                              setFuriganaValue(furiganaNewValue);
                            }}
                          >
                            <Radio
                              checked={isSelected(furiganaSelection, 'new')}
                              onChange={(e) => e.stopPropagation()}
                              style={{ marginRight: 8 }}
                            />
                            <div
                              style={{
                                width: 60,
                                marginRight: 8,
                                backgroundColor: isSelected(furiganaSelection, 'new') ? '#0693e3' : 'transparent',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                textAlign: 'center'
                              }}
                            >
                              <Text style={{
                                color: isSelected(furiganaSelection, 'new') ? 'white' : 'rgba(0, 0, 0, 0.45)',
                                fontWeight: isSelected(furiganaSelection, 'new') ? 'bold' : 'normal'
                              }}>新規</Text>
                            </div>
                            {isSelected(furiganaSelection, 'new') ? (
                              <div style={{ flex: 1 }}>
                                <TextArea
                                  placeholder={`${furiganaLabel}を入力`}
                                  autoSize={{ minRows: 1, maxRows: 3 }}
                                  value={furiganaValue}
                                  onChange={(e) => {
                                    setFuriganaValue(e.target.value);
                                    setFuriganaNewValue(e.target.value);
                                  }}
                                  onBlur={(e) => {
                                    // フォーカスを外した時にひらがなをカタカナに変換
                                    const convertedValue = hiraganaToKatakana(e.target.value);
                                    setFuriganaValue(convertedValue);
                                    setFuriganaNewValue(convertedValue);
                                  }}
                                />
                              </div>
                            ) : (
                              <div style={{ flex: 1, padding: '4px 8px' }}>
                                <Text style={{
                                  color: 'rgba(0, 0, 0, 0.65)'
                                }}>
                                  {furiganaNewValue || '-'}
                                </Text>
                              </div>
                            )}
                          </div>

                          {/* 2行目: 既存データ (APIデータ) */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px',
                              backgroundColor: isSelected(furiganaSelection, 'existing') ? '#0693e3' : '#f9f9f9',
                              border: `1px solid ${isSelected(furiganaSelection, 'existing') ? '#0693e3' : '#d9d9d9'}`,
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              // すでに選択されている場合は処理をスキップ
                              if (furiganaSelection === 'existing') {
                                console.log('EditResultPage - フリガナ：すでに既存が選択されているため処理をスキップ');
                                return;
                              }
                              setFuriganaSelection('existing');
                              // furiganaValueは設定しない（選択UIを消さないため）
                            }}
                          >
                            <Radio
                              checked={isSelected(furiganaSelection, 'existing')}
                              onChange={(e) => e.stopPropagation()}
                              style={{ marginRight: 8 }}
                            />
                            <div
                              style={{
                                width: 60,
                                marginRight: 8,
                                backgroundColor: isSelected(furiganaSelection, 'existing') ? '#0693e3' : 'transparent',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                textAlign: 'center'
                              }}
                            >
                              <Text style={{
                                color: isSelected(furiganaSelection, 'existing') ? 'white' : 'rgba(0, 0, 0, 0.45)',
                                fontWeight: isSelected(furiganaSelection, 'existing') ? 'bold' : 'normal'
                              }}>既存</Text>
                            </div>
                            <div style={{ flex: 1, padding: '4px 8px' }}>
                              <Text style={{
                                color: isSelected(furiganaSelection, 'existing') ? 'white' : 'rgba(0, 0, 0, 0.65)'
                              }}>
                                {furiganaExistingValue || '-'}
                              </Text>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // 既存データがない場合は通常の入力フィールド
                        <TextArea
                          placeholder={`${furiganaLabel}を入力`}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                          value={furiganaValue}
                          onChange={(e) => {
                            setFuriganaValue(e.target.value);
                            setFuriganaNewValue(e.target.value);
                          }}
                          onBlur={(e) => {
                            // フォーカスを外した時にひらがなをカタカナに変換
                            const convertedValue = hiraganaToKatakana(e.target.value);
                            setFuriganaValue(convertedValue);
                            setFuriganaNewValue(convertedValue);
                          }}
                        />
                      )}
                    </div>
                  </React.Fragment>
                );
              }

              return fieldElement;
            })}

            {/* 本登録モード（仮登録顧客一覧からの編集）時のみ、ランク・備考・拡張フィールドを下部に追加 */}
            {isPermanentMode && (
              <AdditionalInfoSection
                form={form}
                rankFieldName="rankno"
                remarksFieldName="notes"
                optionFieldValues={permanentOptionFieldValues}
                onOptionFieldValuesChange={setPermanentOptionFieldValues}
                existingRemarks={permanentHasExisting ? permanentExistingRemarks : undefined}
                newRemarks={permanentNewRemarks}
                remarksNewChecked={permanentRemarksNewChecked}
                remarksExistingChecked={permanentRemarksExistingChecked}
                onRemarksNewCheckedChange={setPermanentRemarksNewChecked}
                onRemarksExistingCheckedChange={setPermanentRemarksExistingChecked}
                showMergeRemarks
                mergeRemarksChecked={permanentDoMergeRemarks}
                onMergeRemarksChange={handlePermanentDoMergeRemarksChange}
              />
            )}
          </Form>
        </Card>
      </Content>
      
      {/* フラットなフルワイドボタンエリア */}
      <div style={{
        display: 'flex',
        position: 'sticky',
        bottom: isInFlutter ? `${flutterFooterHeight}px` : 0,
        width: '100%',
        zIndex: 10
      }}>
        {isPermanentMode ? (
          // 本登録モード: 「一覧に戻る」ボタン
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleReturnToTempCustomerList}
            style={{
              flex: '1 1 50%',
              height: '56px',
              borderRadius: 0,
              borderTop: '1px solid #e8e8e8',
              borderRight: '1px solid #e8e8e8',
              borderBottom: 'none',
              borderLeft: 'none',
              boxShadow: 'none',
            }}
          >
            一覧に戻る
          </Button>
        ) : !isManualInput && (
          <Button
            type="default"
            icon={<CameraOutlined />}
            onClick={handleRetake}
            style={{
              flex: '1 1 50%',
              height: '56px',
              borderRadius: 0,
              borderTop: '1px solid #e8e8e8',
              borderRight: '1px solid #e8e8e8',
              borderBottom: 'none',
              borderLeft: 'none',
              boxShadow: 'none'
            }}
          >
            再撮影
          </Button>
        )}
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSubmit}
          loading={loading || submittingToApi || advancingToNext}
          style={{
            flex: isManualInput ? '1 1 100%' : '1 1 50%',
            height: '56px',
            borderRadius: 0,
            borderTop: '1px solid #0693e3',
            borderBottom: 'none',
            boxShadow: 'none'
          }}
        >
          {(() => {
            // 本登録モード（仮登録顧客一覧の編集）の場合
            if (isPermanentMode) {
              return isInFlutter ? (
                <>
                  本登録して<br />次へ
                </>
              ) : (
                '本登録して次へ'
              );
            }
            // 緊急モードは追加情報画面をスキップしてフォロー登録へ（skipadditionalinfoflag と同じ扱い）
            if (user?.skipadditionalinfoflag || emergencyMode) {
              return isInFlutter ? (
                <>
                  登録して<br />フォロー登録へ
                </>
              ) : (
                '登録してフォロー登録へ'
              );
            }
            return isInFlutter ? (
              <>
                登録して<br />追加情報へ
              </>
            ) : (
              '登録して追加情報へ'
            );
          })()}
        </Button>
      </div>
      
      {/* 緊急モード：既存顧客検知の通知モーダル */}
      <Modal
        title="既存顧客が見つかりました"
        open={emergencyModalOpen}
        onOk={() => setEmergencyModalOpen(false)}
        onCancel={() => setEmergencyModalOpen(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
      >
        <p>既存顧客が見つかったため、そのまま「登録してフォロー登録へ」ボタンを押して進んでください。</p>
      </Modal>

      {/* 通報モーダル */}
      <Modal
        title="AI読み取り結果の通報"
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setReportModalVisible(false)}>
            キャンセル
          </Button>,
          <Button key="submit" type="primary" onClick={submitReport}>
            送信
          </Button>
        ]}
      >
        <Paragraph>
          OCRの読み取り結果に誤りがある場合、システム改善のために通報いただけます。
          どのような問題がありましたか？
        </Paragraph>
        <TextArea
          rows={4}
          value={reportContent}
          onChange={e => setReportContent(e.target.value)}
          placeholder="例: 会社名が正しく認識されていない、住所の一部が欠けているなど"
        />
      </Modal>

      {/* 住所自動入力確認モーダル */}
      <Modal
        centered
        open={postalCodeConfirmVisible}
        title="確認"
        okText="はい"
        cancelText="いいえ"
        onOk={() => {
          form.setFieldsValue({ address: newAddress });
          message.success('住所を更新しました');
          setNewAddress('');
          setPostalCodeConfirmVisible(false);
        }}
        onCancel={() => {
          setNewAddress('');
          setPostalCodeConfirmVisible(false);
        }}
      >
        <p>郵便番号が変わりました。新しい住所({newAddress})に変更しますか？</p>
      </Modal>

      {/* 名刺表面 拡大プレビュー */}
      <BusinessCardImageModal
        open={frontPreviewOpen}
        src={showPermCardImageToggle ? (permSelectedFront ?? undefined) : scanResult?.data?.businesscardfrontimage}
        alt="撮影した名刺"
        flutterFooterHeight={flutterFooterHeight}
        onClose={() => setFrontPreviewOpen(false)}
      />
      {/* 名刺裏面 拡大プレビュー */}
      <BusinessCardImageModal
        open={backPreviewOpen}
        src={backSideImage ?? undefined}
        alt="名刺の裏面"
        flutterFooterHeight={flutterFooterHeight}
        onClose={() => setBackPreviewOpen(false)}
      />

      {/* 再解析中モーダル（高品質再解析の進行中表示） */}
      {rescanning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '24px 32px',
              minWidth: 240,
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Spin size="large" />
            <div style={{ marginTop: 16, fontWeight: 'bold' }}>再解析中...</div>
            <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
              完了まで少しお待ちください
            </div>
            <div style={{ marginTop: 16 }}>
              <Button onClick={handleCancelRescan}>キャンセル</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default EditResultPage;